import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import DateRangeField from '../components/DateRangeField';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Save, Building2, Globe, CalendarClock,
  CreditCard, MapPin, Phone, FileText, Briefcase, Loader2,
  CreditCard as CardIcon, Banknote, AlertCircle, Check, Download, Mail,
  Plus, Pencil, Trash2, Tag, Bell, TrendingUp, RefreshCw, ClipboardList,
  UserCog, UserCheck, Shield, Eye, EyeOff, XCircle, Ban,
  // Leave type icons
  Palmtree, Plane, Umbrella, Sun, Sunset, Mountain, Waves, Tent,
  Heart, HeartPulse, Stethoscope, Pill, Thermometer, Activity, Cross,
  Baby, Home, UserPlus, Users2, HeartHandshake,
  Laptop, Coffee, Monitor, Wifi, Building,
  GraduationCap, BookOpen, Award, School,
  Gift, PartyPopper, Cake, Star,
  Car, Train, Ship,
  Snowflake, CloudRain, Leaf,
  Bed, Moon, Sofa,
  Dumbbell, Bike, Trophy,
  Briefcase as BriefcaseIcon, Calculator, Clipboard,
  CircleDot, CalendarDays, CalendarCheck, CalendarX, CalendarClock as CalendarClockIcon,
  // Religion icons
  Church, Sparkles, Flame, FlameKindling, HandHeart, Sunrise, Feather, Flower2,
  Upload, ShieldCheck, Blocks, AlertTriangle
} from 'lucide-react';
import TimetasticImport from '../components/TimetasticImport';
import SecurityTab from '../components/SecurityTab';
// ⚠️ Its own component, not another 200 lines in here — SettingsPage.js is already over
// 4,200 lines and CLAUDE.md says to stop adding to it.
import IntegrationsTab from '../components/IntegrationsTab';
import DigestTeamsRow, { SendTestDigest, TeamsChannelPicker, useTeamsChannels } from '../components/DigestTeamsRow';
import DigestSlackRow, { SlackChannelPicker, useSlackChannels } from '../components/DigestSlackRow';
import CloseAccountDialog from '../components/CloseAccountDialog';
// H9 (13/10/2026): the leave type's three-way approval choice and its rules panel.
import { ApprovalChoice, CountsAndPrivacy } from '../components/LeaveTypeRules';
import { badgeFor } from '../lib/approvalMode';
import { ACCOUNT_STATUS_CHANGED } from '../components/AccountClosingBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import Sidebar from '../components/Sidebar';
import LeaveCalculator from '../components/LeaveCalculator';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage, settingsApi, dashboardApi, billingApi, leaveApi, blackoutsApi, departmentsApi, usersApi, locationsApi, tagsApi, leaveLimitsApi, holidaysApi, entitlementApi } from '../lib/api';
import WorkingHoursEditor, { computeWeeklyHours } from '../components/WorkingHoursEditor';
// ⚠️ Pure, and CHECKED — `node src/lib/companyHolidays.checks.mjs`. "Which holidays are
// OURS" has been answered wrongly in three different places; it is answered here now.
import {
  companyBankHolidays, headlineScopeLabel, otherCountryCodes,
} from '../lib/companyHolidays';
import { settingsSavedMessage } from '../lib/workingDays';
import { carryUseByMonth } from '../lib/leaveYear';
import CarryExpiryDialog from '../components/CarryExpiryDialog';
import ToilSettingsCard from '../components/toil/ToilSettingsCard';
import RolloverPanel from '../components/RolloverPanel';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
// ⚠️ The SAME county control the Locations screen uses — one vocabulary, one list.
import CountyField from '../components/CountyField';
import PerfectionBox, { PERFECTION_LABEL } from '../components/PerfectionBox';
import { MONTHLY_PER_USER, ANNUAL_PER_USER, ANNUAL_SAVING, ANNUAL_DISCOUNT_PERCENT } from '../lib/pricing';

// Comprehensive color palette for leave types
const COLOR_OPTIONS = [
  // Reds
  { value: '#fecaca', name: 'Red Light' },
  { value: '#f87171', name: 'Red 400' },
  { value: '#ef4444', name: 'Red' },
  { value: '#dc2626', name: 'Red Dark' },
  { value: '#991b1b', name: 'Red Deep' },
  // Oranges
  { value: '#fed7aa', name: 'Orange Light' },
  { value: '#fb923c', name: 'Orange 400' },
  { value: '#f97316', name: 'Orange' },
  { value: '#ea580c', name: 'Orange Dark' },
  { value: '#9a3412', name: 'Orange Deep' },
  // Ambers/Yellows
  { value: '#fde68a', name: 'Amber Light' },
  { value: '#fbbf24', name: 'Amber 400' },
  { value: '#f59e0b', name: 'Amber' },
  { value: '#eab308', name: 'Yellow' },
  { value: '#ca8a04', name: 'Yellow Dark' },
  // Limes/Greens
  { value: '#bef264', name: 'Lime Light' },
  { value: '#84cc16', name: 'Lime' },
  { value: '#65a30d', name: 'Lime Dark' },
  { value: '#4ade80', name: 'Green Light' },
  { value: '#22c55e', name: 'Green' },
  { value: '#16a34a', name: 'Green Dark' },
  { value: '#166534', name: 'Green Deep' },
  // Teals/Emeralds
  { value: '#5eead4', name: 'Teal Light' },
  { value: '#14b8a6', name: 'Teal' },
  { value: '#0d9488', name: 'Teal Dark' },
  { value: '#6ee7b7', name: 'Emerald Light' },
  { value: '#10b981', name: 'Emerald' },
  { value: '#059669', name: 'Emerald Dark' },
  // Cyans/Sky Blues
  { value: '#67e8f9', name: 'Cyan Light' },
  { value: '#06b6d4', name: 'Cyan' },
  { value: '#0891b2', name: 'Cyan Dark' },
  { value: '#7dd3fc', name: 'Sky Light' },
  { value: '#0ea5e9', name: 'Sky' },
  { value: '#0284c7', name: 'Sky Dark' },
  // Blues
  { value: '#93c5fd', name: 'Blue Light' },
  { value: '#60a5fa', name: 'Blue 400' },
  { value: '#3b82f6', name: 'Blue' },
  { value: '#2563eb', name: 'Blue Dark' },
  { value: '#1d4ed8', name: 'Blue Deep' },
  // Indigos
  { value: '#a5b4fc', name: 'Indigo Light' },
  { value: '#818cf8', name: 'Indigo 400' },
  { value: '#6366f1', name: 'Indigo' },
  { value: '#4f46e5', name: 'Indigo Dark' },
  { value: '#3730a3', name: 'Indigo Deep' },
  // Note: Purple/Violet colors removed - reserved for Bank Holidays
  // Fuchsias/Pinks
  { value: '#f0abfc', name: 'Fuchsia Light' },
  { value: '#d946ef', name: 'Fuchsia' },
  { value: '#c026d3', name: 'Fuchsia Dark' },
  { value: '#f9a8d4', name: 'Pink Light' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#db2777', name: 'Pink Dark' },
  // Roses
  { value: '#fda4af', name: 'Rose Light' },
  { value: '#fb7185', name: 'Rose 400' },
  { value: '#f43f5e', name: 'Rose' },
  { value: '#e11d48', name: 'Rose Dark' },
  // Neutrals & Earth Tones
  { value: '#a8a29e', name: 'Stone Light' },
  { value: '#78716c', name: 'Stone' },
  { value: '#57534e', name: 'Stone Dark' },
  { value: '#9ca3af', name: 'Gray Light' },
  { value: '#6b7280', name: 'Gray' },
  { value: '#4b5563', name: 'Gray Dark' },
  { value: '#94a3b8', name: 'Slate Light' },
  { value: '#64748b', name: 'Slate' },
  { value: '#475569', name: 'Slate Dark' },
  // Browns & Warm Neutrals
  { value: '#d6d3d1', name: 'Warm Gray' },
  { value: '#a3a3a3', name: 'Neutral' },
  { value: '#737373', name: 'Neutral Dark' },
  { value: '#92400e', name: 'Brown' },
  { value: '#78350f', name: 'Brown Dark' },
];

// Comprehensive icon options for leave types
const ICON_OPTIONS = [
  // Vacation & Travel
  { value: 'palmtree', name: 'Palm Tree', icon: Palmtree, category: 'Vacation' },
  { value: 'plane', name: 'Plane', icon: Plane, category: 'Vacation' },
  { value: 'umbrella', name: 'Beach Umbrella', icon: Umbrella, category: 'Vacation' },
  { value: 'sun', name: 'Sun', icon: Sun, category: 'Vacation' },
  { value: 'sunset', name: 'Sunset', icon: Sunset, category: 'Vacation' },
  { value: 'mountain', name: 'Mountain', icon: Mountain, category: 'Vacation' },
  { value: 'waves', name: 'Waves', icon: Waves, category: 'Vacation' },
  { value: 'tent', name: 'Camping', icon: Tent, category: 'Vacation' },
  
  // Medical & Health
  { value: 'heart', name: 'Heart', icon: Heart, category: 'Medical' },
  { value: 'heartpulse', name: 'Heart Pulse', icon: HeartPulse, category: 'Medical' },
  { value: 'stethoscope', name: 'Stethoscope', icon: Stethoscope, category: 'Medical' },
  { value: 'pill', name: 'Pill', icon: Pill, category: 'Medical' },
  { value: 'thermometer', name: 'Thermometer', icon: Thermometer, category: 'Medical' },
  { value: 'activity', name: 'Activity', icon: Activity, category: 'Medical' },
  { value: 'cross', name: 'Medical Cross', icon: Cross, category: 'Medical' },
  
  // Family & Personal
  { value: 'baby', name: 'Baby', icon: Baby, category: 'Family' },
  { value: 'home', name: 'Home', icon: Home, category: 'Family' },
  { value: 'userplus', name: 'New Person', icon: UserPlus, category: 'Family' },
  { value: 'users2', name: 'Family', icon: Users2, category: 'Family' },
  { value: 'hearthandshake', name: 'Compassionate', icon: HeartHandshake, category: 'Family' },
  
  // Work & Remote
  { value: 'laptop', name: 'Laptop', icon: Laptop, category: 'Work' },
  { value: 'coffee', name: 'Coffee', icon: Coffee, category: 'Work' },
  { value: 'monitor', name: 'Monitor', icon: Monitor, category: 'Work' },
  { value: 'wifi', name: 'Remote', icon: Wifi, category: 'Work' },
  { value: 'building', name: 'Office', icon: Building, category: 'Work' },
  { value: 'briefcase', name: 'Briefcase', icon: BriefcaseIcon, category: 'Work' },
  
  // Education & Training
  { value: 'graduationcap', name: 'Graduation', icon: GraduationCap, category: 'Education' },
  { value: 'bookopen', name: 'Study', icon: BookOpen, category: 'Education' },
  { value: 'award', name: 'Award', icon: Award, category: 'Education' },
  { value: 'school', name: 'School', icon: School, category: 'Education' },
  
  // Celebrations
  { value: 'gift', name: 'Gift', icon: Gift, category: 'Celebrations' },
  { value: 'partypopper', name: 'Party', icon: PartyPopper, category: 'Celebrations' },
  { value: 'cake', name: 'Birthday', icon: Cake, category: 'Celebrations' },
  { value: 'star', name: 'Star', icon: Star, category: 'Celebrations' },
  
  // Transport
  { value: 'car', name: 'Car', icon: Car, category: 'Transport' },
  { value: 'train', name: 'Train', icon: Train, category: 'Transport' },
  { value: 'ship', name: 'Ship', icon: Ship, category: 'Transport' },
  
  // Weather/Seasons
  { value: 'snowflake', name: 'Winter', icon: Snowflake, category: 'Seasons' },
  { value: 'cloudrain', name: 'Rain', icon: CloudRain, category: 'Seasons' },
  { value: 'leaf', name: 'Autumn', icon: Leaf, category: 'Seasons' },
  
  // Rest & Wellness
  { value: 'bed', name: 'Rest', icon: Bed, category: 'Wellness' },
  { value: 'moon', name: 'Night', icon: Moon, category: 'Wellness' },
  { value: 'sofa', name: 'Relax', icon: Sofa, category: 'Wellness' },
  { value: 'dumbbell', name: 'Fitness', icon: Dumbbell, category: 'Wellness' },
  { value: 'bike', name: 'Cycling', icon: Bike, category: 'Wellness' },
  
  // Calendar & General
  { value: 'calendar', name: 'Calendar', icon: Calendar, category: 'General' },
  { value: 'calendardays', name: 'Calendar Days', icon: CalendarDays, category: 'General' },
  { value: 'calendarcheck', name: 'Approved', icon: CalendarCheck, category: 'General' },
  { value: 'calendarx', name: 'Cancelled', icon: CalendarX, category: 'General' },
  { value: 'clock', name: 'Clock', icon: Clock, category: 'General' },
  { value: 'circledot', name: 'Dot', icon: CircleDot, category: 'General' },
  
  // Religion & Faith
  { value: 'church', name: 'Church', icon: Church, category: 'Religion' },
  { value: 'religioncross', name: 'Cross', icon: Cross, category: 'Religion' },
  { value: 'sparkles', name: 'Spiritual', icon: Sparkles, category: 'Religion' },
  { value: 'flame', name: 'Flame', icon: Flame, category: 'Religion' },
  { value: 'flamekindling', name: 'Candle Light', icon: FlameKindling, category: 'Religion' },
  { value: 'handheart', name: 'Blessing', icon: HandHeart, category: 'Religion' },
  { value: 'sunrise', name: 'Sunrise', icon: Sunrise, category: 'Religion' },
  { value: 'crescent', name: 'Crescent Moon', icon: Moon, category: 'Religion' },
  { value: 'starfaith', name: 'Star of Faith', icon: Star, category: 'Religion' },
  { value: 'feather', name: 'Feather', icon: Feather, category: 'Religion' },
  { value: 'flower2', name: 'Lotus', icon: Flower2, category: 'Religion' },
];

// Helper to get icon component by value
const getIconComponent = (iconValue) => {
  const iconOption = ICON_OPTIONS.find(i => i.value === iconValue);
  return iconOption ? iconOption.icon : Calendar;
};

// 1st, 2nd, 3rd, 4th … 11th, 12th, 13th, 21st. Hoisted function declaration,
// same reason as payrollTreatmentLabel below.
function ordinal(n) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
}

// Payroll pay treatment, in words (DESIGN-payroll.md decision B).
// A function declaration at module scope on purpose: it is used inside the
// leave-types list far below, and a `const` arrow read above its own declaration
// parses perfectly and throws the instant the page loads (CLAUDE.md rule 11).
function payrollTreatmentLabel(treatment) {
  if (treatment === 'unpaid') return 'unpaid';
  if (treatment === 'statutory') return 'statutory';
  return 'paid';
}

// Group icons by category
const ICON_CATEGORIES = [...new Set(ICON_OPTIONS.map(i => i.category))];

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [options, setOptions] = useState(null);
  const [billing, setBilling] = useState(null);
  // Self-service off-boarding (17 Jul): the "Close account" flow.
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeStatus, setCloseStatus] = useState(null);   // {closing, purge_on, days_left}
  const [reinstating, setReinstating] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Carry-over expiry Save dialog (DESIGN-rollover.md, 09/10/2026): the expiry the server
  // holds, and — when a change reaches people holding carried-over days — who and how.
  const [savedExpiryMonths, setSavedExpiryMonths] = useState(null);
  const [expiryImpact, setExpiryImpact] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Business Hours state
  const [businessHours, setBusinessHours] = useState({
    monday: { enabled: true, start: '09:00', end: '17:30' },
    tuesday: { enabled: true, start: '09:00', end: '17:30' },
    wednesday: { enabled: true, start: '09:00', end: '17:30' },
    thursday: { enabled: true, start: '09:00', end: '17:30' },
    friday: { enabled: true, start: '09:00', end: '17:30' },
    saturday: { enabled: false, start: '09:00', end: '17:30' },
    sunday: { enabled: false, start: '09:00', end: '17:30' },
  });
  const [defaultHoursPerWeek, setDefaultHoursPerWeek] = useState(37.5);
  const [defaultBreakMinutes, setDefaultBreakMinutes] = useState(60);
  // Employment-law facts from the backend (null = country not modelled = show no
  // statutory content, D9), and the tenant's REAL bank holidays for this year —
  // replaces the hard-coded "8 bank holidays" that was wrong for Scotland (9) & NI (10).
  const [jurisdiction, setJurisdiction] = useState(null);
  const [bankHolidays, setBankHolidays] = useState([]);
  // Which country/region the headline figure belongs to, and whether the company has
  // offices elsewhere. ⚠️ Rob's option A, 07/08/2026: a multi-country company is told
  // WHOSE number this is rather than being handed one figure that is only true for the
  // head office. A UK-only company sees no caveat — most customers are UK-only.
  const [holidayScope, setHolidayScope] = useState({ label: '', elsewhere: [] });
  // ⚠️ FALL BACK TO THE CODE, NEVER TO A BLANK. "Algeria" is what an admin wants;
  // "DZ" is still usable; an empty string would read as a rendering fault. Same rule
  // as the Locations page uses for its country picker.
  const countryName = (code) =>
    options?.countries?.find((c) => c.code === code)?.name || code;
  // Company-wide statutory check (ADVISORY, D4). null jurisdiction = unmodelled
  // country = the card renders nothing at all (D9).
  const [entitlementAdvisory, setEntitlementAdvisory] = useState(null);
  // ADVISORY rest-break warnings from the backend — the law lives in
  // services/jurisdictions.py, never here (see the LeaveCalculator.jsx audit finding).
  const [hoursWarnings, setHoursWarnings] = useState([]);

  // Live advisory check as the pattern is edited (debounced). [] can mean
  // "compliant" or "country not modelled" — both correctly render as nothing.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      settingsApi.checkWorkingHours({ pattern: businessHours })
        .then((res) => setHoursWarnings(res.data?.rest_break_warnings || []))
        .catch(() => setHoursWarnings([]));
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessHours, loading]);

  // Leave type dialog state
  const [leaveTypeDialogOpen, setLeaveTypeDialogOpen] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [leaveTypeForm, setLeaveTypeForm] = useState({
    name: '',
    color: '#f59e0b',
    icon: 'calendar',
    deducted: true,
    requires_approval: true,
    auto_approve: false,
    include_max_off: true,
    is_private: false,
    limit_days: '',
    // Payroll (DESIGN-payroll.md decision B)
    payroll_include: true,
    payroll_pay_treatment: 'paid',
    payroll_is_sickness: false,
    payroll_allow_override: false
  });
  
  // Blackout periods state
  const [blackouts, setBlackouts] = useState([]);
  const [blackoutDialogOpen, setBlackoutDialogOpen] = useState(false);
  const [editingBlackout, setEditingBlackout] = useState(null);
  const [blackoutForm, setBlackoutForm] = useState({
    name: '',
    reason: '',
    start_date: '',
    end_date: '',
    scope: 'company',
    department_id: '',
    user_id: '',
    is_recurring: false
  });
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myManageScope, setMyManageScope] = useState('all');
  const [myManagedDeptIds, setMyManagedDeptIds] = useState([]);
  useEffect(() => {
    const role = (user?.role || '').toLowerCase();
    if (role !== 'manager') { setMyManageScope('all'); return; }
    const uid = user?.id || user?.user_id;
    if (!uid) return;
    usersApi.get(uid).then(res => {
      const d = res.data || {};
      setMyManageScope(d.manager_manage_scope || 'own');
      setMyManagedDeptIds(d.managed_department_ids || []);
    }).catch(() => {});
  }, [user]);
  
  // Active tab from URL
  const activeTab = searchParams.get('tab') || 'general';

  // ---- Leave limits (concurrent-off rules) ----
  const [limits, setLimits] = useState([]);
  const [limitOpts, setLimitOpts] = useState({ departments: [], locations: [], tags: [] });
  const [newLimit, setNewLimit] = useState({ scope: 'department', target_id: '', target_role: 'manager', max_off: 1, name: '' });
  const [savingLimit, setSavingLimit] = useState(false);

  useEffect(() => {
    // Also on Leave Types: the "approved automatically" panel lists the limits (H9).
    if (activeTab !== 'limits' && activeTab !== 'leavetypes') return;
    leaveLimitsApi.list().then(r => setLimits(r.data || [])).catch(() => {});
    Promise.all([
      departmentsApi.list().catch(() => ({ data: [] })),
      locationsApi.list().catch(() => ({ data: [] })),
      tagsApi.list().catch(() => ({ data: [] })),
    ]).then(([d, b, t]) => setLimitOpts({ departments: d.data || [], locations: b.data || [], tags: t.data || [] }));
  }, [activeTab]);

  // Refetch the statutory advisory EVERY time the Leave Policy tab is opened —
  // allowances change from the Employees page, and a once-per-page-load fetch
  // showed Rob a stale "14 days" after he'd already fixed it to 28.
  useEffect(() => {
    if (activeTab !== 'leave') return;
    entitlementApi.advisory()
      .then((r) => setEntitlementAdvisory(r.data?.jurisdiction ? r.data : null))
      .catch(() => {});
  }, [activeTab]);

  const reloadLimits = async () => { const r = await leaveLimitsApi.list(); setLimits(r.data || []); };
  const handleCreateLimit = async (e) => {
    e.preventDefault();
    const payload = { scope: newLimit.scope, max_off: parseInt(newLimit.max_off) || 0, name: newLimit.name || null };
    if (['department', 'location', 'tag'].includes(newLimit.scope)) {
      if (!newLimit.target_id) { toast.error('Please choose a target'); return; }
      payload.target_id = newLimit.target_id;
    }
    if (newLimit.scope === 'role') payload.target_role = newLimit.target_role;
    setSavingLimit(true);
    try {
      await leaveLimitsApi.create(payload);
      toast.success('Leave limit added');
      setNewLimit({ scope: 'department', target_id: '', target_role: 'manager', max_off: 1, name: '' });
      reloadLimits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add limit'); }
    finally { setSavingLimit(false); }
  };
  const handleDeleteLimit = async (id) => {
    if (!window.confirm('Delete this leave limit?')) return;
    try { await leaveLimitsApi.delete(id); reloadLimits(); } catch (e) { toast.error('Failed to delete'); }
  };
  const sessionId = searchParams.get('session_id');
  const paymentStatus = searchParams.get('status');
  const gcReturn = searchParams.get('gc');
  const portalReturn = searchParams.get('billing_portal_return');
  
  const setActiveTab = (tab) => {
    setSearchParams(tab === 'general' ? {} : { tab });
  };
  
  // Form data for General tab
  const [generalForm, setGeneralForm] = useState({
    company_name: '',
    domain: '',
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    city: '',
    county: '',
    postcode: '',
    company_phone: '',
    company_website: '',
    industry: '',
    employee_count: '',
    vat_number: '',
    company_reg_number: '',
    country_code: 'GB',
    timezone: 'Europe/London'
  });
  
  // Form data for Leave Policy tab
  const [leaveForm, setLeaveForm] = useState({
    leave_year_start_month: 1,
    // Payroll (DESIGN-payroll.md F1 and D)
    pay_period_type: 'monthly',
    pay_period_start_day: 0,
    sickness_count_frame: 'leave_year',
    default_annual_allowance: 28,
    public_holiday_region: 'england-and-wales',
    bank_holidays_included: false,
    part_time_bank_holidays: 'as_they_fall',
    // Accrual Settings
    pro_rata_enabled: true,
    // Carry-Over Rules
    carry_over_enabled: false,
    carry_over_max_days: 5,
    carry_over_expiry_months: 3,
    carry_over_expiry_emails: true,
    break_alert_days: 90,
    // Booking Rules
    min_notice_days: 0,
    max_consecutive_days: 0,
    max_advance_booking_months: 0,
    allow_half_days: true,
    booking_granularity: 'half_day',
    allow_hourly_bookings: false,
    // Years of Service Policy - Simple Mode
    service_bonus_enabled: false,
    service_bonus_days_per_year: 1,
    service_bonus_min_years: 0,
    service_bonus_max_days: 5,
    // Years of Service Policy - Tiered Mode
    service_bonus_use_tiers: false,
    service_bonus_tiers: '[]'
  });
  
  // Form data for Birthday Reminders tab
  const [birthdayForm, setBirthdayForm] = useState({
    birthday_reminders_enabled: false,
    birthday_notify_all_staff: false,
    birthday_notify_admins: false,
    birthday_notify_manager: true,
    birthday_notify_department: false
  });
  
  // Form data for Manager Permissions tab
  const [managerSettingsForm, setManagerSettingsForm] = useState({
    mgr_can_view_team_balances: true,
    mgr_can_approve_leave: true,
    mgr_can_view_leave_reasons: true,
    mgr_can_edit_team_details: false,
    mgr_can_view_personal_info: false,
    mgr_can_cancel_approved_leave: false,
    mgr_can_book_team_leave: false,
    mgr_can_approve_own_leave: false,
    mgr_receive_notifications: true,
    mgr_max_approval_days: 0,
    mgr_can_create_blackouts: false,
    mgr_can_view_reports: false,
    mgr_can_view_sickness: false,
    mgr_view_all_departments: false,
    mgr_manage_all_departments: false
  });
  const [managerSettingsLoading, setManagerSettingsLoading] = useState(false);
  const [mgrList, setMgrList] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyIds, setApplyIds] = useState([]);
  const [applyChanges, setApplyChanges] = useState({});
  const [resetConfirm, setResetConfirm] = useState({ open: false, ids: [], name: '' });
  const managerSettingsSnapshot = useRef({});
  
  // Form data for Email Notifications tab
  // ⚠️ Loaded ONCE for the whole Notifications tab and handed to all three digest rows.
  // Three cards each fetching /teams/channels is three times the work for one answer, and
  // they could disagree with each other mid-save.
  const teamsChannels = useTeamsChannels();
  // ⚠️ Slack build step 4: the same once-for-the-tab rule. `slackChannels` is null while
  // loading and `{ connected: false }` for a company without Slack (which then sees nothing).
  const { slack: slackChannels, refresh: refreshSlackChannels,
          refreshing: refreshingSlackChannels } = useSlackChannels();
  // Whether any chat row sits on a digest card — the test button then hangs off the last one
  // rather than drawing a divider of its own.
  const digestChatRowsShown = (Array.isArray(teamsChannels) && teamsChannels.length > 0)
    || !!slackChannels?.connected;

  const [notificationsForm, setNotificationsForm] = useState({
    // (email_timezone is gone — one company timezone, in General. A9, 21/10/2026.)
    // ⚠️ One channel for the company — see the picker beside the timezone.
    teams_channel_id: null,
    // ⚠️ Slack build step 4 — '' is "no channel", which the server reads as "clear it".
    slack_channel_id: '',
    // Daily Digest
    daily_digest_enabled: false,
    daily_digest_time: '08:00',
    daily_digest_to_teams: false,
    daily_digest_to_slack: false,
    daily_digest_notify_admins: true,
    daily_digest_notify_managers: false,
    daily_digest_notify_all_staff: false,
    // Weekly Digest
    weekly_digest_enabled: false,
    weekly_digest_to_teams: false,
    weekly_digest_to_slack: false,
    weekly_digest_day: 'monday',
    weekly_digest_time: '08:00',
    weekly_digest_notify_admins: true,
    weekly_digest_notify_managers: false,
    weekly_digest_notify_all_staff: false,
    // Monthly Digest
    monthly_digest_enabled: false,
    monthly_digest_to_teams: false,
    monthly_digest_to_slack: false,
    monthly_digest_day: 1,
    monthly_digest_time: '08:00',
    monthly_digest_notify_admins: true,
    monthly_digest_notify_managers: false,
    monthly_digest_notify_all_staff: false
  });
  
  // Billing form

  useEffect(() => {
    fetchData();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Stripe redirect with session_id
  useEffect(() => {
    const verifyPayment = async () => {
      if (sessionId && paymentStatus === 'success') {
        try {
          toast.loading('Verifying payment...', { id: 'payment-verify' });
          const response = await billingApi.getCheckoutStatus(sessionId);

          // A COMPLETED checkout means the subscription is set up. The backend
          // reconciles it from Stripe on this same call, so the tenant is active
          // even for Bacs Direct Debit — a delayed-notification method whose
          // payment_status is never 'paid' at this point (the first collection
          // clears days later), so we key off the session being complete.
          const done = response.data.status === 'complete';
          const paidNow = response.data.payment_status === 'paid';
          if (done) {
            toast.success(
              paidNow
                ? 'Payment successful! Your subscription is now active.'
                : 'Direct Debit set up — your subscription is active. Your first payment is scheduled.',
              { id: 'payment-verify', duration: 5000 });
            // Refresh billing data (now reflects the reconciled subscription)
            const billingRes = await billingApi.getOverview().catch(() => ({ data: null }));
            setBilling(billingRes.data);
            // Clear the URL params
            setSearchParams({ tab: 'billing' });
          } else {
            toast.info('Payment is being processed...', { id: 'payment-verify' });
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          toast.error('Could not verify payment status', { id: 'payment-verify' });
        }
      } else if (paymentStatus === 'cancelled') {
        toast.warning('Payment was cancelled');
        setSearchParams({ tab: 'billing' });
      }
    };
    
    verifyPayment();
  // `setSearchParams` is stable across renders — listing it is noise, not safety.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paymentStatus]);

  // GoCardless: the customer just returned from the hosted mandate flow.
  useEffect(() => {
    if (gcReturn === 'cancelled') {
      toast.info('Direct Debit setup was cancelled.');
      setSearchParams({ tab: 'billing' });
      return;
    }
    if (gcReturn === 'return') {
      (async () => {
        try {
          toast.loading('Confirming your Direct Debit\u2026', { id: 'gc-confirm' });
          const res = await billingApi.confirmGoCardless();
          if (res.data.status === 'ok') {
            toast.success('Direct Debit set up \u2014 your account is active', { id: 'gc-confirm' });
            // Hard reload to a clean URL so the app-wide paywall/banner re-read the now-active status.
            window.location.replace('/settings?tab=billing');
            return;
          }
          toast.info('Your Direct Debit is still being set up.', { id: 'gc-confirm' });
          setSearchParams({ tab: 'billing' });
          fetchData();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Could not confirm Direct Debit', { id: 'gc-confirm' });
          setSearchParams({ tab: 'billing' });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gcReturn]);

  // Returned from the Stripe Customer Portal — reconcile with Stripe so a change
  // made there (cancel, plan switch, new card) shows straight away, then drop the
  // marker from the URL. Belt-and-braces alongside the webhook, not a replacement.
  useEffect(() => {
    if (portalReturn !== '1') return;
    (async () => {
      try {
        await billingApi.resyncMyBilling();
      } catch (e) {
        // A resync hiccup isn't fatal — the webhook / nightly job still reconcile.
        console.error('Billing resync on portal return failed:', e);
      } finally {
        const billingRes = await billingApi.getOverview().catch(() => ({ data: null }));
        if (billingRes.data) setBilling(billingRes.data);
        setSearchParams({ tab: 'billing' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalReturn]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, optionsRes, approvalsRes, billingRes, leaveTypesRes, businessHoursRes, blackoutsRes, departmentsRes, employeesRes, jurisdictionRes, holidaysRes] = await Promise.all([
        settingsApi.get(),
        settingsApi.getOptions(),
        dashboardApi.getPendingApprovals(),
        billingApi.getOverview().catch(() => ({ data: null })),
        leaveApi.getTypes().catch(() => ({ data: [] })),
        settingsApi.getBusinessHours().catch(() => ({ data: null })),
        blackoutsApi.list(true).catch(() => ({ data: [] })),
        departmentsApi.list().catch(() => ({ data: [] })),
        usersApi.list({ is_active: true }).catch(() => ({ data: [] })),
        // Law comes from the backend (D9) — no rule pack = all nulls = show nothing
        settingsApi.getJurisdiction().catch(() => ({ data: null })),
        holidaysApi.list(new Date().getFullYear()).catch(() => ({ data: [] }))
      ]);
      // (The statutory advisory is fetched by its own effect, re-fetched every
      // time the Leave Policy tab opens — allowances change from other pages.)
      
      setJurisdiction(jurisdictionRes.data && jurisdictionRes.data.jurisdiction ? jurisdictionRes.data : null);
      // /holidays without a user_id returns ROWS FOR EVERY COUNTRY AND REGION THE
      // COMPANY HAS AN OFFICE IN — correct for the Holidays page, which must show them
      // all. This screen wants ONE headline figure, so it filters to the company's own
      // country and region and counts DISTINCT DATES.
      //
      // ⚠️ THE FILTER USED TO ASK ONLY ABOUT THE REGION, and Rob's Leave Policy panel
      // read "30 days + 44 bank holidays = 74 total days off". Non-UK holidays are
      // stored with NO REGION (a region is a UK sub-division), so France's 11,
      // Algeria's 14 and Saudi Arabia's 13 all passed a test that was only looking for
      // "not Scotland". Same blindness as F51 and F52.
      const tenantRegion = settingsRes.data?.public_holiday_region || 'england-and-wales';
      setBankHolidays(companyBankHolidays(
        holidaysRes.data, settingsRes.data?.country_code, tenantRegion));
      setHolidayScope({
        label: headlineScopeLabel(
          jurisdictionRes.data?.jurisdiction || settingsRes.data?.country_code,
          tenantRegion),
        elsewhere: otherCountryCodes(holidaysRes.data, settingsRes.data?.country_code),
      });
      setSettings(settingsRes.data);
      setOptions(optionsRes.data);
      setPendingCount(approvalsRes.data.length);
      setBilling(billingRes.data);
      // Off-boarding: is this account already scheduled to close? (cheap, admin-only)
      billingApi.accountStatus().then((r) => setCloseStatus(r.data)).catch(() => {});
      setLeaveTypes(leaveTypesRes.data || []);
      setBlackouts(blackoutsRes.data || []);
      setDepartments(departmentsRes.data || []);
      setEmployees(employeesRes.data || []);
      
      // Populate Business Hours
      if (businessHoursRes.data) {
        setBusinessHours(businessHoursRes.data.business_hours || businessHours);
        setDefaultHoursPerWeek(businessHoursRes.data.default_hours_per_week || 37.5);
        if (businessHoursRes.data.default_break_minutes !== undefined && businessHoursRes.data.default_break_minutes !== null) {
          setDefaultBreakMinutes(businessHoursRes.data.default_break_minutes);
        }
      }
      
      // Populate General form
      setGeneralForm({
        company_name: settingsRes.data.company_name || '',
        domain: settingsRes.data.domain || '',
        address_line_1: settingsRes.data.address_line_1 || '',
        address_line_2: settingsRes.data.address_line_2 || '',
        address_line_3: settingsRes.data.address_line_3 || '',
        city: settingsRes.data.city || '',
        county: settingsRes.data.county || '',
        postcode: settingsRes.data.postcode || '',
        company_phone: settingsRes.data.company_phone || '',
        company_website: settingsRes.data.company_website || '',
        industry: settingsRes.data.industry || '',
        employee_count: settingsRes.data.employee_count || '',
        vat_number: settingsRes.data.vat_number || '',
        company_reg_number: settingsRes.data.company_reg_number || '',
        country_code: settingsRes.data.country_code || 'GB',
        timezone: settingsRes.data.timezone || 'Europe/London'
      });
      
      // What the server holds for the carry-over expiry — so Save can tell whether it is
      // being CHANGED, and ask who that reaches (DESIGN-rollover.md, 09/10/2026).
      setSavedExpiryMonths(settingsRes.data.carry_over_expiry_months ?? 3);
      // Populate Leave form
      setLeaveForm({
        leave_year_start_month: settingsRes.data.leave_year_start_month || 1,
        pay_period_type: settingsRes.data.pay_period_type || 'monthly',
        // ⚠️ `??` not `||`. Zero means "calendar month" and is a real answer —
        // `0 || 0` happens to be harmless, but the next person changing this
        // default would be walking into Rob's zero all over again (13/08/2026).
        pay_period_start_day: settingsRes.data.pay_period_start_day ?? 0,
        sickness_count_frame: settingsRes.data.sickness_count_frame || 'leave_year',
        default_annual_allowance: settingsRes.data.default_annual_allowance || 28,
        public_holiday_region: settingsRes.data.public_holiday_region || 'england-and-wales',
        bank_holidays_included: settingsRes.data.bank_holidays_included || false,
        // ⚠️ `||` IS RIGHT HERE, NOT `??`. A NULL from the server means
        // "never chosen", which resolves to the usual arrangement — the same
        // answer `services/entitlement.part_time_model` gives on the backend.
        part_time_bank_holidays: settingsRes.data.part_time_bank_holidays || 'as_they_fall',
        // Accrual Settings
        pro_rata_enabled: settingsRes.data.pro_rata_enabled !== false,
        // Carry-Over Rules
        carry_over_enabled: settingsRes.data.carry_over_enabled || false,
        // ⚠️ `??` NOT `||` (09/10/2026). Both of these have a real zero — no limit, and
        // never expires — and `|| 5` / `|| 3` turned it back into 5 and 3 on screen, so the
        // next save quietly wrote them back. BUGS.md, carry-over expiry.
        carry_over_max_days: settingsRes.data.carry_over_max_days ?? 5,
        carry_over_expiry_months: settingsRes.data.carry_over_expiry_months ?? 3,
        carry_over_expiry_emails: settingsRes.data.carry_over_expiry_emails !== false,
        break_alert_days: settingsRes.data.break_alert_days ?? 90,
        // Booking Rules
        min_notice_days: settingsRes.data.min_notice_days || 0,
        max_consecutive_days: settingsRes.data.max_consecutive_days || 0,
        max_advance_booking_months: settingsRes.data.max_advance_booking_months || 0,
        allow_half_days: settingsRes.data.allow_half_days !== false,
        booking_granularity: settingsRes.data.booking_granularity || 'half_day',
        allow_hourly_bookings: settingsRes.data.allow_hourly_bookings || false,
        // Years of Service Policy - Simple Mode
        service_bonus_enabled: settingsRes.data.service_bonus_enabled || false,
        service_bonus_days_per_year: settingsRes.data.service_bonus_days_per_year || 1,
        service_bonus_min_years: settingsRes.data.service_bonus_min_years || 0,
        service_bonus_max_days: settingsRes.data.service_bonus_max_days || 5,
        // Years of Service Policy - Tiered Mode
        service_bonus_use_tiers: settingsRes.data.service_bonus_use_tiers || false,
        service_bonus_tiers: settingsRes.data.service_bonus_tiers || '[]'
      });
      
      // Populate Birthday form
      setBirthdayForm({
        birthday_reminders_enabled: settingsRes.data.birthday_reminders_enabled || false,
        birthday_notify_all_staff: settingsRes.data.birthday_notify_all_staff || false,
        birthday_notify_admins: settingsRes.data.birthday_notify_admins || false,
        birthday_notify_manager: settingsRes.data.birthday_notify_manager ?? true,
        birthday_notify_department: settingsRes.data.birthday_notify_department || false
      });
      
      // Populate Notifications form
      setNotificationsForm({
        // ⚠️ **THIS LIST IS EXPLICIT, SO A NEW FIELD THAT IS NOT ADDED HERE LOADS AS ITS
        // DEFAULT FOREVER.** The screen would show "off" for a company that had switched
        // it on, saving would then write "off" back, and the setting would appear not to
        // stick — with nothing in any log. Adding the column and the API field is not
        // enough on its own.
        teams_channel_id: settingsRes.data.teams_channel_id || null,
        daily_digest_to_teams: settingsRes.data.daily_digest_to_teams || false,
        weekly_digest_to_teams: settingsRes.data.weekly_digest_to_teams || false,
        monthly_digest_to_teams: settingsRes.data.monthly_digest_to_teams || false,
        slack_channel_id: settingsRes.data.slack_channel_id || '',
        daily_digest_to_slack: settingsRes.data.daily_digest_to_slack || false,
        weekly_digest_to_slack: settingsRes.data.weekly_digest_to_slack || false,
        monthly_digest_to_slack: settingsRes.data.monthly_digest_to_slack || false,
        daily_digest_enabled: settingsRes.data.daily_digest_enabled || false,
        daily_digest_time: settingsRes.data.daily_digest_time || '08:00',
        daily_digest_notify_admins: settingsRes.data.daily_digest_notify_admins ?? true,
        daily_digest_notify_managers: settingsRes.data.daily_digest_notify_managers || false,
        daily_digest_notify_all_staff: settingsRes.data.daily_digest_notify_all_staff || false,
        weekly_digest_enabled: settingsRes.data.weekly_digest_enabled || false,
        weekly_digest_day: settingsRes.data.weekly_digest_day || 'monday',
        weekly_digest_time: settingsRes.data.weekly_digest_time || '08:00',
        weekly_digest_notify_admins: settingsRes.data.weekly_digest_notify_admins ?? true,
        weekly_digest_notify_managers: settingsRes.data.weekly_digest_notify_managers || false,
        weekly_digest_notify_all_staff: settingsRes.data.weekly_digest_notify_all_staff || false,
        monthly_digest_enabled: settingsRes.data.monthly_digest_enabled || false,
        monthly_digest_day: settingsRes.data.monthly_digest_day || 1,
        monthly_digest_time: settingsRes.data.monthly_digest_time || '08:00',
        monthly_digest_notify_admins: settingsRes.data.monthly_digest_notify_admins ?? true,
        monthly_digest_notify_managers: settingsRes.data.monthly_digest_notify_managers || false,
        monthly_digest_notify_all_staff: settingsRes.data.monthly_digest_notify_all_staff || false
      });
      
      // Populate Manager Settings form
      const _mgrForm = {
        mgr_can_view_team_balances: settingsRes.data.mgr_can_view_team_balances ?? true,
        mgr_can_approve_leave: settingsRes.data.mgr_can_approve_leave ?? true,
        mgr_can_view_leave_reasons: settingsRes.data.mgr_can_view_leave_reasons ?? true,
        mgr_can_edit_team_details: settingsRes.data.mgr_can_edit_team_details ?? false,
        mgr_can_view_personal_info: settingsRes.data.mgr_can_view_personal_info ?? false,
        mgr_can_cancel_approved_leave: settingsRes.data.mgr_can_cancel_approved_leave ?? false,
        mgr_can_book_team_leave: settingsRes.data.mgr_can_book_team_leave ?? false,
        mgr_can_approve_own_leave: settingsRes.data.mgr_can_approve_own_leave ?? false,
        mgr_receive_notifications: settingsRes.data.mgr_receive_notifications ?? true,
        mgr_max_approval_days: settingsRes.data.mgr_max_approval_days ?? 0,
        mgr_can_create_blackouts: settingsRes.data.mgr_can_create_blackouts ?? false,
        mgr_can_view_reports: settingsRes.data.mgr_can_view_reports ?? false,
        mgr_can_view_sickness: settingsRes.data.mgr_can_view_sickness ?? false,
        mgr_view_all_departments: settingsRes.data.mgr_view_all_departments ?? false,
        mgr_manage_all_departments: settingsRes.data.mgr_manage_all_departments ?? false
      };
      setManagerSettingsForm(_mgrForm);
      managerSettingsSnapshot.current = { ..._mgrForm };
      
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...generalForm,
        // Optional integer: send a number or null, never an empty string (422)
        employee_count:
          generalForm.employee_count === '' || generalForm.employee_count == null
            ? null
            : parseInt(generalForm.employee_count, 10),
      };
      await settingsApi.update(payload);
      toast.success('Company information saved');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? (detail[0]?.msg || 'Validation error') : (detail || 'Failed to save settings');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLeave = async (e) => {
    e.preventDefault();
    // ⚠️ CARRY-OVER EXPIRY CHANGED? Rob, 09/10/2026: tell the admin who it affects and let
    // them choose. Only when somebody already holds carried-over days this leave year —
    // otherwise there is nobody to ask about and Save just saves.
    const months = parseInt(leaveForm.carry_over_expiry_months, 10) || 0;
    if (savedExpiryMonths !== null && months !== (parseInt(savedExpiryMonths, 10) || 0)) {
      try {
        const res = await settingsApi.carryExpiryImpact(months);
        if (res?.data?.people?.length) {
          setExpiryImpact(res.data);
          return;   // the dialog's Save carries on with the admin's choice
        }
      } catch (err) {
        // Can't tell who it reaches: save with no choice, which is decision c on the
        // server (more time applies now, less time waits) — never the harsh one.
      }
    }
    await saveLeave(leaveForm);
  };

  const saveLeave = async (payload) => {
    setSaving(true);
    try {
      // An emptied "Getting a break" box means "leave it as it is", not a broken save.
      const body = { ...payload, break_alert_days: payload.break_alert_days === '' ? null : payload.break_alert_days };
      const res = await settingsApi.update(body);
      // Leave policy is the screen most likely to move everybody's numbers — bank
      // holidays included/on top, the default allowance, the service bonus. Say what it
      // did rather than leaving a stale-balance note for somebody to find later.
      toast.success(settingsSavedMessage(
        'Leave policy saved',
        res?.data?.repriced_count,
        res?.data?.repriced_in_background));
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBirthday = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.update(birthdayForm);
      toast.success('Birthday reminder settings saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.update(notificationsForm);
      toast.success('Email notification settings saved');
      fetchData();
      // The Slack picker's warning and "last posted" belong to the channel now saved.
      if (slackChannels?.connected) refreshSlackChannels();
    } catch (error) {
      // ⚠️ The server's own sentence when it has one — a Slack channel Slack wouldn't confirm
      // says why ("invite HolidayHive to it first"), where a bare "failed" would say nothing.
      toast.error(apiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveManagerSettings = async (e) => {
    e.preventDefault();
    setManagerSettingsLoading(true);
    try {
      await settingsApi.updateManagerSettings(managerSettingsForm);
      toast.success('Manager permission settings saved');
      // Work out which settings actually changed, so we can offer to apply just those to custom managers
      const _snap = managerSettingsSnapshot.current || {};
      const _applyKeys = [
        'mgr_can_view_team_balances','mgr_can_approve_leave','mgr_can_view_leave_reasons',
        'mgr_can_edit_team_details','mgr_can_view_personal_info','mgr_can_cancel_approved_leave',
        'mgr_can_book_team_leave','mgr_can_approve_own_leave','mgr_receive_notifications',
        'mgr_max_approval_days','mgr_can_create_blackouts','mgr_can_view_reports',
        'mgr_can_view_sickness',
      ];
      const _changes = {};
      _applyKeys.forEach((k) => { if (managerSettingsForm[k] !== _snap[k]) _changes['mgr_override_' + k.slice(4)] = managerSettingsForm[k]; });
      managerSettingsSnapshot.current = { ...managerSettingsForm };
      fetchData();
      const _r = await usersApi.list({ is_active: true });
      const _m = (_r.data || []).filter((u) => u.role === 'manager');
      setMgrList(_m);
      const _c = _m.filter((x) => x.use_global_manager_settings === false);
      if (_c.length && Object.keys(_changes).length) { setApplyChanges(_changes); setApplyIds([]); setApplyOpen(true); }
    } catch (error) {
      toast.error('Failed to save manager settings');
    } finally {
      setManagerSettingsLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const res = await usersApi.list({ is_active: true });
      setMgrList((res.data || []).filter((u) => u.role === 'manager'));
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadManagers(); }, []);

  const reachLabel = (sc) => sc === 'all' ? 'All departments' : sc === 'specific' ? 'Specific' : 'Own department';
  const isCustomMgr = (m) => m.use_global_manager_settings === false;

  const resetManagersToGlobal = async (ids) => {
    if (!ids || !ids.length) return;
    try {
      await settingsApi.managersFollowGlobal(ids);
      toast.success(ids.length === 1 ? 'Manager reset to global settings' : `${ids.length} managers reset to global`);
      await loadManagers();
    } catch (e) { toast.error('Failed to reset'); }
  };

  const applyChangesToManagers = async (ids, changes) => {
    if (!ids || !ids.length || !changes || !Object.keys(changes).length) return;
    try {
      await Promise.all(ids.map((id) => usersApi.update(id, changes)));
      toast.success(ids.length === 1 ? 'Change applied to manager' : `Change applied to ${ids.length} managers`);
      await loadManagers();
    } catch (e) { toast.error('Failed to apply change'); }
  };

  const handleStripeCheckout = async (planType) => {
    try {
      const response = await billingApi.createStripeCheckout(planType);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  // Direct Debit routes to whichever provider is the active DD supplier: GoCardless
  // when it's enabled in superadmin, otherwise Stripe (Bacs). Same subscription
  // engine either way; only one DD supplier is ever live (backend enforces it).
  const handleDirectDebit = async (planType) => {
    try {
      if (billing?.dd_provider === 'gocardless') {
        const response = await billingApi.setupGoCardless(planType);
        if (response.data.status === 'redirect' && response.data.authorisation_url) {
          window.location.href = response.data.authorisation_url;
          return;
        }
        // Mid-trial plan swap done in place on the existing mandate — no redirect.
        if (response.data.status === 'switched') {
          toast.success(`Switched to the ${response.data.plan} plan — your Direct Debit stays as-is.`);
          const billingRes = await billingApi.getOverview().catch(() => ({ data: null }));
          if (billingRes.data) setBilling(billingRes.data);
          return;
        }
        if (response.data.status === 'unchanged') {
          toast.info(`You're already on the ${response.data.plan} plan.`);
          return;
        }
        if (response.data.status === 'not_configured') {
          toast.info(response.data.message);
        }
        return;
      }
      const response = await billingApi.createStripeCheckout(planType, 'bacs');
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up Direct Debit');
    }
  };

  // Paying (post-trial) customer switching plan. Unlike a trial switch (instant,
  // free), this is SCHEDULED for the current period end with no proration — if
  // they committed to annual, monthly starts when the annual term ends, and vice
  // versa. We keep them on their current plan until then and show a pending banner.
  const handleScheduledSwitch = async (planType) => {
    try {
      const response = await billingApi.switchPlan(planType);
      const st = response.data?.status;
      if (st === 'scheduled') {
        const eff = response.data?.effective ? formatDateLongUK(response.data.effective) : 'your renewal date';
        const planWord = response.data?.plan === 'annual' ? 'Annual' : 'Monthly';
        toast.success(`Your plan will switch to ${planWord} on ${eff}. Nothing changes until then.`);
      } else if (st === 'cancelled') {
        toast.success('Scheduled change cancelled — you\'ll stay on your current plan.');
      } else if (st === 'unchanged') {
        toast.info(`You're already on the ${response.data?.plan} plan.`);
      }
      const billingRes = await billingApi.getOverview().catch(() => ({ data: null }));
      if (billingRes.data) setBilling(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule the plan change');
    }
  };

  const handleCancelScheduledSwitch = async () => {
    try {
      await billingApi.cancelScheduledSwitch();
      toast.success('Scheduled change cancelled — you\'ll stay on your current plan.');
      const billingRes = await billingApi.getOverview().catch(() => ({ data: null }));
      if (billingRes.data) setBilling(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel the scheduled change');
    }
  };

  // Leave type handlers
  const openLeaveTypeDialog = (leaveType = null) => {
    if (leaveType) {
      setEditingLeaveType(leaveType);
      setLeaveTypeForm({
        name: leaveType.name,
        color: leaveType.color,
        icon: leaveType.icon || 'calendar',
        deducted: leaveType.deducted,
        requires_approval: leaveType.requires_approval,
        // ⚠️ These four are SENT by the API since 13/10/2026. Before then the dialog opened
        // every type with them blank or defaulted, and saving reset them (BUGS.md).
        auto_approve: leaveType.auto_approve ?? false,
        include_max_off: leaveType.include_max_off ?? true,
        is_private: leaveType.is_private ?? false,
        limit_days: leaveType.limit_days ?? '',
        // ?? rather than || — payroll_include is a boolean where false is a real
        // answer, and `false || true` is true. That exact shape has bitten this
        // project before (Rob's zero, 13/08/2026).
        payroll_include: leaveType.payroll_include ?? true,
        payroll_pay_treatment: leaveType.payroll_pay_treatment || 'paid',
        payroll_is_sickness: leaveType.payroll_is_sickness ?? false,
        payroll_allow_override: leaveType.payroll_allow_override ?? false
      });
    } else {
      setEditingLeaveType(null);
      setLeaveTypeForm({
        name: '',
        color: '#f59e0b',
        icon: 'calendar',
        deducted: true,
        requires_approval: true,
        auto_approve: false,
        include_max_off: true,
        is_private: false,
        limit_days: '',
        payroll_include: true,
        payroll_pay_treatment: 'paid',
        payroll_is_sickness: false,
        payroll_allow_override: false
      });
    }
    setLeaveTypeDialogOpen(true);
  };

  const handleSaveLeaveType = async () => {
    if (!leaveTypeForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        name: leaveTypeForm.name,
        color: leaveTypeForm.color,
        icon: leaveTypeForm.icon,
        deducted: leaveTypeForm.deducted,
        requires_approval: leaveTypeForm.requires_approval,
        auto_approve: !!leaveTypeForm.auto_approve && leaveTypeForm.requires_approval !== false,
        // ⚠️ Were hard-coded true / false here — every save reset them (BUGS.md, 13/10/2026).
        include_max_off: leaveTypeForm.include_max_off !== false,
        is_private: !!leaveTypeForm.is_private,
        limit_days: leaveTypeForm.limit_days !== '' && leaveTypeForm.limit_days != null
          && parseFloat(leaveTypeForm.limit_days) > 0 ? parseFloat(leaveTypeForm.limit_days) : null,
        payroll_include: leaveTypeForm.payroll_include,
        payroll_pay_treatment: leaveTypeForm.payroll_pay_treatment,
        payroll_is_sickness: leaveTypeForm.payroll_is_sickness,
        payroll_allow_override: leaveTypeForm.payroll_allow_override,
        // Saving this dialog means the payroll section was on screen and a human
        // has now seen the guessed defaults — so the "not set up for payroll" nag
        // can stop for this type. The nag exists precisely because the migration's
        // defaults are a guess, and it should clear only when someone has looked.
        payroll_reviewed: true
      };
      
      if (editingLeaveType) {
        await leaveApi.updateType(editingLeaveType.id, data);
        toast.success('Leave type updated');
      } else {
        await leaveApi.createType(data);
        toast.success('Leave type created');
      }
      
      setLeaveTypeDialogOpen(false);
      // Refresh leave types
      const res = await leaveApi.getTypes();
      setLeaveTypes(res.data || []);
    } catch (error) {
      toast.error('Failed to save leave type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLeaveType = async (leaveType) => {
    if (!window.confirm(`Delete "${leaveType.name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await leaveApi.deleteType(leaveType.id);
      toast.success('Leave type deleted');
      // Refresh leave types
      const res = await leaveApi.getTypes();
      setLeaveTypes(res.data || []);
    } catch (error) {
      toast.error('Failed to delete leave type');
    }
  };

  // Blackout period handlers
  const openBlackoutDialog = (blackout = null) => {
    if (blackout) {
      setEditingBlackout(blackout);
      setBlackoutForm({
        name: blackout.name,
        reason: blackout.reason || '',
        start_date: blackout.start_date,
        end_date: blackout.end_date,
        scope: blackout.scope,
        department_id: blackout.department_id || '',
        user_id: blackout.user_id || '',
        is_recurring: blackout.is_recurring
      });
    } else {
      setEditingBlackout(null);
      setBlackoutForm({
        name: '',
        reason: '',
        start_date: '',
        end_date: '',
        scope: 'company',
        department_id: '',
        user_id: '',
        is_recurring: false
      });
    }
    setBlackoutDialogOpen(true);
  };

  const handleSaveBlackout = async () => {
    if (!blackoutForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!blackoutForm.start_date || !blackoutForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        name: blackoutForm.name,
        reason: blackoutForm.reason || null,
        start_date: blackoutForm.start_date,
        end_date: blackoutForm.end_date,
        scope: blackoutForm.scope,
        department_id: blackoutForm.scope === 'department' ? blackoutForm.department_id : null,
        user_id: blackoutForm.scope === 'user' ? blackoutForm.user_id : null,
        is_recurring: blackoutForm.is_recurring
      };
      
      if (editingBlackout) {
        await blackoutsApi.update(editingBlackout.id, data);
        toast.success('No-leave dates updated');
      } else {
        await blackoutsApi.create(data);
        toast.success('No-leave dates added');
      }
      
      setBlackoutDialogOpen(false);
      // Refresh blackouts
      const res = await blackoutsApi.list(true);
      setBlackouts(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save the no-leave dates');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlackout = async (blackout) => {
    if (!window.confirm(`Delete "${blackout.name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await blackoutsApi.delete(blackout.id);
      toast.success('No-leave dates deleted');
      // Refresh blackouts
      const res = await blackoutsApi.list(true);
      setBlackouts(res.data || []);
    } catch (error) {
      toast.error('Failed to delete the no-leave dates');
    }
  };

  const handleToggleBlackoutActive = async (blackout) => {
    try {
      await blackoutsApi.update(blackout.id, { is_active: !blackout.is_active });
      toast.success(blackout.is_active ? 'No-leave dates turned off' : 'No-leave dates turned on');
      // Refresh blackouts
      const res = await blackoutsApi.list(true);
      setBlackouts(res.data || []);
    } catch (error) {
      toast.error('Failed to update the no-leave dates');
    }
  };

  const handleSaveBusinessHours = async () => {
    setSaving(true);
    try {
      const res = await settingsApi.updateBusinessHours({
        monday: businessHours.monday,
        tuesday: businessHours.tuesday,
        wednesday: businessHours.wednesday,
        thursday: businessHours.thursday,
        friday: businessHours.friday,
        saturday: businessHours.saturday,
        sunday: businessHours.sunday,
        // Fallback only — the server DERIVES weekly hours from the pattern (D10).
        default_hours_per_week: computeWeeklyHours(businessHours, null) || defaultHoursPerWeek,
        default_break_minutes: defaultBreakMinutes,
      });
      if (res.data?.default_hours_per_week) setDefaultHoursPerWeek(res.data.default_hours_per_week);
      toast.success('Business hours saved successfully');
    } catch (error) {
      console.error('Failed to save business hours:', error);
      toast.error('Failed to save business hours');
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessHoursDay = (day, field, value) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderLeaveLimitsTab = () => (
    <div className="space-y-6">
      <div className="card-hive p-6">
        <h3 className="font-heading font-bold text-lg mb-1">Leave limits</h3>
        <p className="text-sm text-muted-foreground mb-5">Cap how many people can be off at once in a group. These are advisory — if a booking would exceed a limit the person is warned and can still ask, and the approver sees it's an exception.</p>
        <form onSubmit={handleCreateLimit} className="flex flex-wrap items-end gap-3 mb-6">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Applies to</label>
            <select value={newLimit.scope} onChange={(e) => setNewLimit({ ...newLimit, scope: e.target.value, target_id: '' })} className="h-10 px-3 rounded-md border border-input bg-background text-sm">
              <option value="department">Department</option>
              <option value="location">Location</option>
              <option value="role">Role</option>
              <option value="tag">Tag</option>
              <option value="company">Everyone</option>
            </select>
          </div>
          {['department', 'location', 'tag'].includes(newLimit.scope) && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Which one</label>
              <select value={newLimit.target_id} onChange={(e) => setNewLimit({ ...newLimit, target_id: e.target.value })} className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]">
                <option value="">Select...</option>
                {(newLimit.scope === 'department' ? limitOpts.departments : newLimit.scope === 'location' ? limitOpts.locations : limitOpts.tags).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}
          {newLimit.scope === 'role' && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Role</label>
              <select value={newLimit.target_role} onChange={(e) => setNewLimit({ ...newLimit, target_role: e.target.value })} className="h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Max off at once</label>
            <Input type="number" min="1" value={newLimit.max_off} onChange={(e) => setNewLimit({ ...newLimit, max_off: e.target.value })} className="w-28" />
          </div>
          <Button type="submit" disabled={savingLimit}><Plus className="w-4 h-4 mr-2" />Add limit</Button>
        </form>
        {limits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No leave limits yet.</p>
        ) : (
          <div className="space-y-2">
            {limits.map((l) => (
              <div key={l.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="text-sm">
                  <span className="font-medium">{l.label}</span>
                  <span className="text-muted-foreground"> — max {l.max_off} off at once</span>
                </div>
                <button type="button" onClick={() => handleDeleteLimit(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const settingsTabs = [
    // Neutral id on purpose — the label is a single constant in PerfectionBox.jsx so
    // Rob can rename the feature without breaking anything that points at 'health'.
    { id: 'health', label: PERFECTION_LABEL, icon: <Sparkles className="w-4 h-4" /> },
    { id: 'general', label: 'General', icon: <Building2 className="w-4 h-4" /> },
    { id: 'leave', label: 'Leave Policy', icon: <CalendarClock className="w-4 h-4" /> },
    { id: 'approvals', label: 'Approval Flow', icon: <CheckSquare className="w-4 h-4" /> },
    { id: 'leavetypes', label: 'Leave Types', icon: <Tag className="w-4 h-4" /> },
    { id: 'blackouts', label: 'No-leave dates', icon: <Ban className="w-4 h-4" /> },
    { id: 'limits', label: 'Leave limits', icon: <Users2 className="w-4 h-4" /> },
    { id: 'workinghours', label: 'Working Hours', icon: <Clock className="w-4 h-4" /> },
    { id: 'managers', label: 'Manager Permissions', icon: <UserCog className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <ShieldCheck className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { id: 'birthdays', label: 'Birthdays', icon: <Cake className="w-4 h-4" /> },
    { id: 'integrations', label: 'Integrations', icon: <Blocks className="w-4 h-4" /> },
    { id: 'billing', label: 'Billing', icon: <CreditCard className="w-4 h-4" /> },
    { id: 'import', label: 'Import', icon: <Upload className="w-4 h-4" /> },
  ];

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || 
                  user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  // Departments a manager may set a blackout for (admins/all-reach = every dept)
  const canManageAllDepts = isAdmin || myManageScope === 'all';
  const blackoutDeptOptions = canManageAllDepts ? departments : departments.filter(d => myManagedDeptIds.includes(d.id));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  // UK date format helper
  const formatDateUK = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  const formatDateLongUK = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getDaysRemaining = (trialEndDate) => {
    if (!trialEndDate) return 0;
    const end = new Date(trialEndDate);
    const now = new Date();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  };
  
  // Open the Stripe Customer Portal (Phase 5): card, invoices, plan switch, cancel.
  const handleManageBilling = async () => {
    try {
      const response = await billingApi.createPortalSession();
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Could not open the billing portal');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open the billing portal');
    }
  };

  // Direct Debit customers: change bank details = authorise a FRESH mandate
  // (GoCardless mandates can't be edited in place). On return, /gocardless/confirm
  // re-points the subscription onto the new mandate.
  const handleUpdateBankDetails = async () => {
    try {
      const response = await billingApi.updateGoCardlessMandate();
      if (response.data?.status === 'redirect' && response.data?.authorisation_url) {
        window.location.href = response.data.authorisation_url;
        return;
      }
      toast.error('Could not start the bank-details update');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start the bank-details update');
    }
  };

  // Render General Tab
  const renderGeneralTab = () => (
    <form onSubmit={handleSaveGeneral} className="space-y-6">
      {/* Company Details */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Company Details</h2>
            <p className="text-sm text-muted-foreground">Basic information about your organization</p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Company Name *</Label>
            <Input
              value={generalForm.company_name}
              onChange={(e) => setGeneralForm({ ...generalForm, company_name: e.target.value })}
              placeholder="Your Company Ltd"
              className="input-hive mt-1"
              data-testid="company-name-input"
            />
          </div>
          <div>
            <Label>Domain</Label>
            <Input
              value={generalForm.domain}
              onChange={(e) => setGeneralForm({ ...generalForm, domain: e.target.value })}
              placeholder="yourcompany.com"
              className="input-hive mt-1"
            />
          </div>
          <div>
            <Label>Industry</Label>
            <select
              value={generalForm.industry}
              onChange={(e) => setGeneralForm({ ...generalForm, industry: e.target.value })}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Select industry</option>
              {options?.industries?.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Number of Employees</Label>
            <Input
              type="number"
              value={generalForm.employee_count}
              onChange={(e) => setGeneralForm({ ...generalForm, employee_count: e.target.value })}
              placeholder="25"
              className="input-hive mt-1"
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Address</h2>
            <p className="text-sm text-muted-foreground">Company registered address</p>
          </div>
        </div>
        
        <div className="grid gap-4">
          <div>
            <Label>Address Line 1</Label>
            <Input
              value={generalForm.address_line_1}
              onChange={(e) => setGeneralForm({ ...generalForm, address_line_1: e.target.value })}
              placeholder="123 Business Street"
              className="input-hive mt-1"
            />
          </div>
          <div>
            <Label>Address Line 2</Label>
            <Input
              value={generalForm.address_line_2}
              onChange={(e) => setGeneralForm({ ...generalForm, address_line_2: e.target.value })}
              placeholder="Suite 100"
              className="input-hive mt-1"
            />
          </div>
          {/* Added 08/08/2026 — a Location had three address lines and the company record
              had two, so a third line typed on the other screen had nowhere to go. Two
              rooms, at least the same shape now.
              ⚠️ IT SITS WHERE THE LOCATIONS SCREEN PUTS IT — under line 2, above the city.
              It first landed between City and County, which read as nonsense: an address
              that goes City, Address Line 3, County is not an address anyone would write. */}
          <div>
            <Label>Address Line 3</Label>
            <Input
              value={generalForm.address_line_3}
              onChange={(e) => setGeneralForm({ ...generalForm, address_line_3: e.target.value })}
              className="input-hive mt-1"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label>City</Label>
              <Input
                value={generalForm.city}
                onChange={(e) => setGeneralForm({ ...generalForm, city: e.target.value })}
                placeholder="London"
                className="input-hive mt-1"
              />
            </div>
            {/* ⚠️ THE SAME CONTROL AS THE LOCATIONS SCREEN — Rob, 08/08/2026:
                *"The County box in settings → Company Details is not a drop down like the
                one in Locations, but they do update each other."* Two doors onto one
                field offering different vocabularies, so a county typed here might not
                match anything the other screen recognises. */}
            <CountyField
              countryCode={generalForm.country_code}
              value={generalForm.county}
              onChange={(county) => setGeneralForm({ ...generalForm, county })}
            />
            <div>
              <Label>Postcode</Label>
              <Input
                value={generalForm.postcode}
                onChange={(e) => setGeneralForm({ ...generalForm, postcode: e.target.value })}
                placeholder="EC1A 1BB"
                className="input-hive mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact & Regional */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Phone className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Contact & Regional</h2>
            <p className="text-sm text-muted-foreground">Contact information and regional settings</p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Phone</Label>
            <Input
              value={generalForm.company_phone}
              onChange={(e) => setGeneralForm({ ...generalForm, company_phone: e.target.value })}
              placeholder="020 7123 4567"
              className="input-hive mt-1"
            />
          </div>
          <div>
            <Label>Website</Label>
            <Input
              value={generalForm.company_website}
              onChange={(e) => setGeneralForm({ ...generalForm, company_website: e.target.value })}
              placeholder="https://yourcompany.com"
              className="input-hive mt-1"
            />
          </div>
          <div>
            <Label>Country</Label>
            <select
              value={generalForm.country_code}
              onChange={(e) => setGeneralForm({ ...generalForm, country_code: e.target.value })}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
            >
              {options?.countries?.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            {/* ⚠️ THE COMPANY'S ONE TIMEZONE (Group A9, Rob 21/10/2026). Every real
                timezone, from the server (services/timezones.py) — this used to be four
                hand-typed names, one of them ("Europe/Edinburgh") not a timezone at all,
                while Notifications had its own different nine. Year-end rollover, the
                digest emails, reports and calendars all run on this one clock. */}
            <Label>Company timezone</Label>
            <select
              value={generalForm.timezone}
              onChange={(e) => setGeneralForm({ ...generalForm, timezone: e.target.value })}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
              data-testid="company-timezone"
            >
              {['Your country', 'Everywhere else'].map((group) => {
                const items = (options?.timezones || []).filter((tz) => tz.group === group);
                if (!items.length) return null;
                const country = (options?.countries || []).find((c) => c.code === generalForm.country_code);
                const label = group === 'Your country' && country ? `Your country — ${country.name}` : group;
                return (
                  <optgroup key={group} label={label}>
                    {items.map((tz) => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </optgroup>
                );
              })}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Year-end rollover, scheduled emails, reports and your company's Teams/Slack post all run on this clock.
            </p>
          </div>
        </div>
      </div>

      {/* Tax & Registration */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Tax & Registration</h2>
            <p className="text-sm text-muted-foreground">Optional company registration details</p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>VAT Number</Label>
            <Input
              value={generalForm.vat_number}
              onChange={(e) => setGeneralForm({ ...generalForm, vat_number: e.target.value })}
              placeholder="GB123456789"
              className="input-hive mt-1"
            />
          </div>
          <div>
            <Label>Company Registration Number</Label>
            <Input
              value={generalForm.company_reg_number}
              onChange={(e) => setGeneralForm({ ...generalForm, company_reg_number: e.target.value })}
              placeholder="12345678"
              className="input-hive mt-1"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-general-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>
    </form>
  );

  // Render Approval Flow Tab - links to dedicated page
  const renderApprovalsTab = () => (
    <div className="space-y-6">
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <CheckSquare className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Multi-Level Approval Workflows</h2>
            <p className="text-sm text-muted-foreground">Configure who needs to approve leave requests</p>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4 mb-4">
          <h3 className="font-medium mb-2">How it works:</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Create approval policies with one or more approval levels</li>
            <li>• Each level must approve before moving to the next</li>
            <li>• Policies can be set company-wide, per-department, or per-employee</li>
            {/* ⚠️ Replaced 13/10/2026 (FEATURES-OUTSTANDING C4): this line promised "backup
                approvers for when primary approvers are unavailable", which has never been
                true — someone on holiday is chased and then escalated, not rerouted. */}
            <li>• A leave type can approve itself when a request breaks none of the rules —{' '}
              <button type="button" className="underline text-primary" onClick={() => setActiveTab('leavetypes')}>Leave Types</button></li>
            <li>• Automatic email reminders for pending approvals</li>
          </ul>
        </div>
        
        <Link
          to="/settings/approval-policies"
          className="btn-hive-primary inline-flex items-center gap-2"
          data-testid="manage-approval-policies-btn"
        >
          <Settings className="w-4 h-4" />
          Manage Approval Policies
        </Link>
      </div>
    </div>
  );

  // Render Leave Policy Tab
  const renderLeaveTab = () => (
    <>
    {/* Outside the form on purpose: its buttons must never submit it. */}
    <CarryExpiryDialog
      impact={expiryImpact}
      saving={saving}
      onCancel={() => setExpiryImpact(null)}
      onConfirm={async (applyNow) => {
        setExpiryImpact(null);
        await saveLeave({ ...leaveForm, carry_expiry_apply_now: applyNow });
      }}
    />
    <form onSubmit={handleSaveLeave} className="space-y-6">
      {/* Leave Year Section */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <CalendarClock className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Leave Year</h2>
            <p className="text-sm text-muted-foreground">Configure when your leave year starts and resets</p>
          </div>
        </div>
        
        <div>
          <Label>Leave Year Starts</Label>
          <select
            value={leaveForm.leave_year_start_month}
            onChange={(e) => setLeaveForm({ ...leaveForm, leave_year_start_month: parseInt(e.target.value) })}
            className="w-full sm:w-64 h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
          >
            {options?.leave_year_months?.map((m) => (
              <option key={m.value} value={m.value}>{m.name}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">All employee allowances reset on 1st of this month each year</p>
        </div>
      </div>

      {/*
        PAYROLL PERIODS (DESIGN-payroll.md F1 and D).
        Sits under Leave Year because it is the same kind of question — when do
        the company's cycles start — and Settings has thirteen tabs already.
      */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Banknote className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Payroll</h2>
            <p className="text-sm text-muted-foreground">
              How your pay periods run, and how sickness is counted on the payroll report
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label>Pay period</Label>
            <select
              value={leaveForm.pay_period_type}
              onChange={(e) => setLeaveForm({ ...leaveForm, pay_period_type: e.target.value })}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
              data-testid="pay-period-type"
            >
              <option value="monthly">Monthly</option>
              <option value="four_weekly">Every 4 weeks</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {leaveForm.pay_period_type === 'monthly' ? (
            <div>
              <Label>Pay period ends on</Label>
              <select
                value={leaveForm.pay_period_start_day}
                onChange={(e) => setLeaveForm({ ...leaveForm, pay_period_start_day: parseInt(e.target.value) })}
                className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                data-testid="pay-period-day"
              >
                <option value={0}>The last day of the month</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{`The ${ordinal(d)}`}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Paying on the 25th? Choose the 25th — the period then runs the 26th to the 25th.
                {leaveForm.pay_period_start_day > 28 && (
                  <> In shorter months this moves to the last day — February ends on the 28th.</>
                )}
              </p>
            </div>
          ) : (
            <div>
              <Label>Periods start on</Label>
              <select
                value={leaveForm.pay_period_start_day}
                onChange={(e) => setLeaveForm({ ...leaveForm, pay_period_start_day: parseInt(e.target.value) })}
                className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                data-testid="pay-period-day"
              >
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </select>
            </div>
          )}

          <div className="sm:col-span-2">
            <Label>Count sickness over</Label>
            <select
              value={leaveForm.sickness_count_frame}
              onChange={(e) => setLeaveForm({ ...leaveForm, sickness_count_frame: e.target.value })}
              className="w-full sm:w-72 h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
              data-testid="sickness-frame"
            >
              <option value="leave_year">The leave year</option>
              <option value="tax_year">The tax year (from 6 April)</option>
              <option value="rolling_12m">A rolling 12 months</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Only affects the running sickness total on the payroll report. Holiday is always counted
              on the leave year, and the column says which dates it covers rather than "year to date".
            </p>
          </div>
        </div>
      </div>

      {/* Default Annual Allowance Section */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Default Annual Allowance</h2>
            <p className="text-sm text-muted-foreground">Base holiday entitlement for new employees</p>
          </div>
        </div>
        
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <Label>Default Allowance (days)</Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              value={leaveForm.default_annual_allowance}
              onChange={(e) => setLeaveForm({ ...leaveForm, default_annual_allowance: parseFloat(e.target.value) })}
              className="input-hive mt-1"
              data-testid="default-annual-allowance"
            />
            <p className="text-xs text-muted-foreground mt-1">This is the default for new employees</p>
          </div>
          
          <div>
            <Label>Bank Holidays</Label>
            <select
              value={leaveForm.bank_holidays_included ? 'included' : 'additional'}
              onChange={(e) => setLeaveForm({ ...leaveForm, bank_holidays_included: e.target.value === 'included' })}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
              data-testid="bank-holidays-handling"
            >
              <option value="additional">Additional to allowance (e.g., 20 days + bank holidays)</option>
              <option value="included">Included in allowance (bank holidays count towards it)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">How bank holidays relate to the allowance above</p>
          </div>
        </div>

        {/* ⚠️ STEP 7 — THE EMPLOYER'S CHOICE, NOT OURS (Rob, 13/08/2026, after reading the
            Citizens Advice page). The app used to impose the pro-rated share on every
            customer, which on an "on top" company quietly took days off a part-timer who
            works Mondays and gave them to one who works Thursdays. Citizens Advice is
            explicit that the plain arrangement is lawful, so it is the contract that
            decides — and the contract is theirs.

            ⚠️ ONLY SHOWN WHEN BANK HOLIDAYS ARE ON TOP. When they are included in the
            allowance both arrangements behave identically — the pot is zero either way and
            everyone loses the ones that land on their days. Offering the choice there
            would be a control that does nothing, which is the exact fault this codebase
            has spent a week deleting. */}
        {!leaveForm.bank_holidays_included ? (
          <div className="mt-4 p-4 rounded-lg border border-border bg-secondary/30">
            <p className="text-sm font-medium">Part-time staff and bank holidays</p>
            <p className="text-sm text-muted-foreground mt-1">
              How should bank holidays work for someone who doesn't work a full week?
            </p>
            <label className="flex items-start gap-2 cursor-pointer mt-3">
              <input
                type="radio"
                className="w-4 h-4 mt-0.5"
                checked={leaveForm.part_time_bank_holidays !== 'equal_share'}
                onChange={() => setLeaveForm({ ...leaveForm, part_time_bank_holidays: 'as_they_fall' })}
                data-testid="part-time-as-they-fall"
              />
              <span className="text-sm">
                <strong>They get the bank holidays that fall on their working days</strong>
                <span className="block text-muted-foreground">
                  The usual arrangement, and what most contracts say. Someone who works
                  Mondays gets more of them than someone who works Thursdays, because
                  that's where the bank holidays fall.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer mt-3">
              <input
                type="radio"
                className="w-4 h-4 mt-0.5"
                checked={leaveForm.part_time_bank_holidays === 'equal_share'}
                onChange={() => setLeaveForm({ ...leaveForm, part_time_bank_holidays: 'equal_share' })}
                data-testid="part-time-equal-share"
              />
              <span className="text-sm">
                <strong>Everyone gets the same share, whichever days they work</strong>
                <span className="block text-muted-foreground">
                  Someone on three days a week gets three fifths of the bank holidays as
                  time they can book whenever they like. Fairer between part-timers; less
                  common, and worth putting in your contracts.
                </span>
              </span>
            </label>
            <p className="text-xs text-muted-foreground mt-3">
              This changes nothing for full-time staff.
            </p>
          </div>
        ) : (
          <div className="mt-4 p-4 rounded-lg border border-border bg-secondary/30">
            <p className="text-sm font-medium">Part-time staff and bank holidays</p>
            <p className="text-sm text-muted-foreground mt-1">
              Bank holidays come out of the allowance, so everyone — full-time or
              part-time — loses the ones that fall on their own working days.
            </p>
          </div>
        )}
        
        {/* Statutory Info Box — facts come from the backend rule pack (D9).
            No rule pack for this tenant's country = NO statutory claims at all,
            never a fallback to UK numbers. The bank-holiday count is the tenant's
            REAL count for this year (the old hard-coded "8" was wrong for
            Scotland (9) and Northern Ireland (10)). */}
        {jurisdiction?.annual_leave && (
          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-blue-400">
              <strong>{jurisdiction.jurisdiction} Statutory Minimum:</strong> Full-time employees are
              entitled to at least {jurisdiction.annual_leave.cap_days} days
              ({jurisdiction.annual_leave.weeks} weeks) paid holiday per year
              {jurisdiction.public_holidays_may_be_included
                ? `, which can include the ${bankHolidays.length || ''} bank holidays`
                : ''}.
              {jurisdiction.annual_leave.source_url && (
                <> <a href={jurisdiction.annual_leave.source_url} target="_blank" rel="noreferrer" className="underline hover:text-blue-300">source</a></>
              )}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Your current setting: <span className="font-medium text-foreground">
                {leaveForm.default_annual_allowance} days {leaveForm.bank_holidays_included ? '(including' : '+'} {bankHolidays.length} bank holidays
                {leaveForm.bank_holidays_included ? ')' : ''} = {leaveForm.bank_holidays_included ? leaveForm.default_annual_allowance : leaveForm.default_annual_allowance + bankHolidays.length} total days off
              </span>
            </p>
            {/* ⚠️ SAY WHOSE FIGURE THIS IS when the company has offices abroad (Rob's
                option A). The statutory sentence above is about ONE country, so the
                total under it has to be as well — and an admin in the Algiers office
                needs to know why their days aren't in it. Nobody's leave is computed
                from this number; the backend resolves each person's own country and
                region. A UK-only company sees nothing extra. */}
            {holidayScope.elsewhere.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {/* ⚠️ TWO THINGS I ONLY SAW BY OPENING THE PAGE (rule 11, rule 12).
                    1. My first wording said "your 5 overseas LOCATIONS" — the number
                       counts distinct COUNTRIES, so two offices in France are one.
                    2. JSX swallows the whitespace between an expression and the text
                       after it, so it rendered "head office.Staff at your 5 other
                       countries(AE, AQ...)". Hence the explicit {' '}.
                    Neither was visible in the code; both were obvious on screen. */}
                Bank holidays shown for {holidayScope.label} — your head office.{' '}
                Staff in your other {holidayScope.elsewhere.length === 1
                  ? 'country' : `${holidayScope.elsewhere.length} countries`}{' '}
                ({holidayScope.elsewhere.map(countryName).join(', ')}) get their own
                country's holidays, counted separately in their own allowance.
              </p>
            )}
          </div>
        )}

        {/* Per-employee statutory check (ADVISORY, D4) — computed from each person's
            ACTUAL working pattern (5.6 × their week, capped), which no competitor
            does. Renders nothing for unmodelled countries (D9). */}
        {entitlementAdvisory && (
          <div className={`mt-4 p-4 rounded-lg border ${
            entitlementAdvisory.below_floor > 0
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-green-500/10 border-green-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <p className={`text-sm font-semibold ${entitlementAdvisory.below_floor > 0 ? 'text-amber-500' : 'text-green-500'}`}>
                {entitlementAdvisory.below_floor > 0
                  ? `${entitlementAdvisory.below_floor} of ${entitlementAdvisory.assessed} employees are below their statutory minimum`
                  : `All ${entitlementAdvisory.assessed} employees meet their statutory minimum`}
              </p>
              <span className="text-xs text-muted-foreground">{entitlementAdvisory.year} · advisory</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Checked against each person's own working pattern — a part-timer's floor is
              {' '}{jurisdiction?.annual_leave?.weeks || ''} weeks of <em>their</em> week, not a full-timer's.
              {entitlementAdvisory.source_url && (
                <> <a href={entitlementAdvisory.source_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">source</a></>
              )}
            </p>
            {entitlementAdvisory.below_floor > 0 && (
              <ul className="mt-3 space-y-1.5">
                {entitlementAdvisory.employees.filter((e) => !e.meets).slice(0, 10).map((e) => (
                  <li key={e.user_id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-background/50">
                    <span className="font-medium">{e.name}</span>
                    <span className="text-muted-foreground">
                      floor {e.statutory_days}d (their days{e.prorated ? ', pro-rated' : ''}) · has {e.total_days}d ·{' '}
                      <span className="text-amber-500 font-medium">{e.shortfall_days}d short</span>
                    </span>
                  </li>
                ))}
                {entitlementAdvisory.employees.filter((e) => !e.meets).length > 10 && (
                  <li className="text-xs text-muted-foreground">…and more</li>
                )}
              </ul>
            )}
            {entitlementAdvisory.below_floor > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Advisory only — nothing is blocked. Contracts and arrangements we can't see may apply;
                fix a shortfall from the employee's profile (allowance or working pattern).
              </p>
            )}
          </div>
        )}
      </div>

      {/* Accrual Settings Section */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">New Starter Allowance</h2>
            <p className="text-sm text-muted-foreground">How allowance is calculated for employees who join part-way through the year</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
            <input
              type="checkbox"
              checked={leaveForm.pro_rata_enabled}
              onChange={(e) => setLeaveForm({ ...leaveForm, pro_rata_enabled: e.target.checked })}
              className="w-5 h-5 rounded border-input accent-primary"
              data-testid="pro-rata-enabled"
            />
            <div>
              <p className="font-medium">Pro-rata for mid-year starters</p>
              <p className="text-sm text-muted-foreground">Automatically calculate reduced allowance for employees starting part-way through the year</p>
            </div>
          </label>
        </div>
      </div>

      {/* Carry-Over Rules Section */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Carry-Over Rules</h2>
            <p className="text-sm text-muted-foreground">What happens to unused leave at year end</p>
          </div>
        </div>
        
        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors mb-4">
          <input
            type="checkbox"
            checked={leaveForm.carry_over_enabled}
            onChange={(e) => setLeaveForm({ ...leaveForm, carry_over_enabled: e.target.checked })}
            className="w-5 h-5 rounded border-input accent-primary"
            data-testid="carry-over-enabled"
          />
          <div>
            <p className="font-medium">Allow carry-over of unused leave</p>
            <p className="text-sm text-muted-foreground">Employees can carry unused days into the next leave year</p>
          </div>
        </label>
        
        {leaveForm.carry_over_enabled && (
          <div className="grid gap-4 sm:grid-cols-2 p-4 bg-secondary/30 rounded-lg">
            <div>
              <Label>Maximum days to carry over</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={leaveForm.carry_over_max_days}
                onChange={(e) => setLeaveForm({ ...leaveForm, carry_over_max_days: parseFloat(e.target.value) || 0 })}
                className="input-hive mt-1"
                data-testid="carry-over-max-days"
              />
              <p className="text-xs text-muted-foreground mt-1">0 = no limit</p>
            </div>
            <div>
              <Label>Use carried-over days within (months)</Label>
              <Input
                type="number"
                min="0"
                max="12"
                value={leaveForm.carry_over_expiry_months}
                onChange={(e) => setLeaveForm({ ...leaveForm, carry_over_expiry_months: Math.min(12, Math.max(0, parseInt(e.target.value, 10) || 0)) })}
                className="input-hive mt-1"
                data-testid="carry-over-expiry"
              />
              {/* ⚠️ THE REAL DATE FOR THIS COMPANY'S LEAVE YEAR (09/10/2026). It said
                  "3 = must use by end of March" whatever the leave year — wrong for an
                  April company, whose 3 months end on 30/06. Same sum as the server. */}
              <p className="text-xs text-muted-foreground mt-1" data-testid="carry-over-expiry-hint">
                {(parseInt(leaveForm.carry_over_expiry_months, 10) || 0) > 0
                  ? <>Carried-over days must be used by <strong>the end of {carryUseByMonth(leaveForm.leave_year_start_month, leaveForm.carry_over_expiry_months)}</strong> — the {ordinal(parseInt(leaveForm.carry_over_expiry_months, 10) || 0)} month of your leave year. Used means days off on or before then. 0 = they never expire.</>
                  : <>Carried-over days never expire. Set a number of months to give them a use-by date.</>}
              </p>
            </div>
            {(parseInt(leaveForm.carry_over_expiry_months, 10) || 0) > 0 && (
              <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={leaveForm.carry_over_expiry_emails !== false}
                  onChange={(e) => setLeaveForm({ ...leaveForm, carry_over_expiry_emails: e.target.checked })}
                  className="w-4 h-4 mt-0.5 rounded border-input accent-primary"
                  data-testid="carry-over-expiry-emails"
                />
                <div>
                  <p className="text-sm font-medium">Email people about their carried-over days</p>
                  <p className="text-xs text-muted-foreground">A month before they expire (only if some aren't booked yet), and the day after if any were lost. In Teams instead for anyone who chose Teams.</p>
                </div>
              </label>
            )}
          </div>
        )}
      </div>

      <RolloverPanel startMonth={leaveForm.leave_year_start_month} />

      {/* Time in lieu (DESIGN-toil.md screen 1). Saves on its own — no <form>, every
          button type="button" — so it can sit inside this tab's form safely. */}
      <ToilSettingsCard />

      {/* Booking Rules Section */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Booking Rules</h2>
            <p className="text-sm text-muted-foreground">Controls and restrictions for leave requests</p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2 mb-4">
          <div>
            <Label>Minimum Notice Required (days)</Label>
            <Input
              type="number"
              min="0"
              value={leaveForm.min_notice_days}
              onChange={(e) => setLeaveForm({ ...leaveForm, min_notice_days: parseInt(e.target.value) || 0 })}
              className="input-hive mt-1"
              data-testid="min-notice-days"
            />
            <p className="text-xs text-muted-foreground mt-1">0 = no minimum notice required</p>
          </div>
          <div>
            <Label>Maximum Consecutive Days</Label>
            <Input
              type="number"
              min="0"
              value={leaveForm.max_consecutive_days}
              onChange={(e) => setLeaveForm({ ...leaveForm, max_consecutive_days: parseInt(e.target.value) || 0 })}
              className="input-hive mt-1"
              data-testid="max-consecutive-days"
            />
            <p className="text-xs text-muted-foreground mt-1">0 = no limit on consecutive days</p>
          </div>
        </div>
        
        <div className="mb-4">
          <Label>Maximum Advance Booking (months)</Label>
          <div className="flex items-center gap-3 mt-1">
            <Input
              type="number"
              min="0"
              max="120"
              value={leaveForm.max_advance_booking_months}
              onChange={(e) => setLeaveForm({ ...leaveForm, max_advance_booking_months: parseInt(e.target.value) || 0 })}
              className="input-hive w-24"
              data-testid="max-advance-booking"
            />
            <span className="text-sm text-muted-foreground">months (0 = unlimited)</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">How far in advance employees and managers can book leave. Admins are not restricted.</p>
        </div>
        
        {/* BOOKABLE GRANULARITY — replaces the old "allow half days" /
            "allow hourly bookings" checkboxes, which nothing ever enforced.
            This one is enforced server-side. Changing it only affects NEW
            bookings; existing bookings keep their stamped cost forever. */}
        <div className="mb-2">
          <Label>Booking granularity</Label>
          <select
            value={leaveForm.booking_granularity || 'half_day'}
            onChange={(e) => setLeaveForm({ ...leaveForm, booking_granularity: e.target.value })}
            className="input-hive mt-1 block w-64 rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="booking-granularity"
          >
            <option value="full_day">Full days only</option>
            <option value="half_day">Half days (default)</option>
            <option value="hour">Down to the hour</option>
            <option value="30m">Down to 30 minutes</option>
            <option value="15m">Down to 15 minutes</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            The smallest amount of leave an employee can book from now on. Timed
            bookings (hour or finer) are single-day and only ever charge time the
            person actually works — never lunch breaks or evenings. Changing this
            never re-prices leave that's already booked.
          </p>
        </div>
      </div>

      {/* Years of Service Policy */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Award className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Years of Service Bonus</h2>
            <p className="text-sm text-muted-foreground">Reward long-serving employees with extra holiday days</p>
          </div>
        </div>
        
        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors mb-4">
          <input
            type="checkbox"
            checked={leaveForm.service_bonus_enabled}
            onChange={(e) => setLeaveForm({ ...leaveForm, service_bonus_enabled: e.target.checked })}
            className="w-5 h-5 rounded border-input accent-primary"
            data-testid="service-bonus-enabled"
          />
          <div>
            <p className="font-medium">Enable Years of Service Bonus</p>
            <p className="text-sm text-muted-foreground">Automatically award extra holiday days based on tenure</p>
          </div>
        </label>
        
        {leaveForm.service_bonus_enabled && (
          <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
            {/* Mode Toggle */}
            <div className="flex gap-2 p-1 bg-secondary rounded-lg w-fit">
              <button
                type="button"
                onClick={() => setLeaveForm({ ...leaveForm, service_bonus_use_tiers: false })}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  !leaveForm.service_bonus_use_tiers 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Simple Mode
              </button>
              <button
                type="button"
                onClick={() => setLeaveForm({ ...leaveForm, service_bonus_use_tiers: true })}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  leaveForm.service_bonus_use_tiers 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Tiered Mode
              </button>
            </div>
            
            {!leaveForm.service_bonus_use_tiers ? (
              /* Simple Mode */
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label>Days per Year of Service</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={leaveForm.service_bonus_days_per_year}
                    onChange={(e) => setLeaveForm({ ...leaveForm, service_bonus_days_per_year: parseFloat(e.target.value) || 0 })}
                    className="input-hive mt-1"
                    data-testid="service-bonus-days"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Extra days for each complete year</p>
                </div>
                <div>
                  <Label>Minimum Years Before Bonus</Label>
                  <Input
                    type="number"
                    min="0"
                    value={leaveForm.service_bonus_min_years}
                    onChange={(e) => setLeaveForm({ ...leaveForm, service_bonus_min_years: parseInt(e.target.value) || 0 })}
                    className="input-hive mt-1"
                    data-testid="service-bonus-min-years"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Years before bonus kicks in</p>
                </div>
                <div>
                  <Label>Maximum Bonus Days</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={leaveForm.service_bonus_max_days}
                    onChange={(e) => setLeaveForm({ ...leaveForm, service_bonus_max_days: parseFloat(e.target.value) || 0 })}
                    className="input-hive mt-1"
                    data-testid="service-bonus-max-days"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Cap on total bonus days</p>
                </div>
              </div>
            ) : (
              /* Tiered Mode */
              <ServiceBonusTiers 
                tiers={leaveForm.service_bonus_tiers} 
                onChange={(tiers) => setLeaveForm({ ...leaveForm, service_bonus_tiers: tiers })}
              />
            )}
            
            {/* Example calculation */}
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm font-medium text-primary mb-2">Example Calculation</p>
              <p className="text-sm text-muted-foreground">
                An employee with <span className="font-medium text-foreground">5 years</span> of service 
                {!leaveForm.service_bonus_use_tiers && leaveForm.service_bonus_min_years > 0 && (
                  <> (after {leaveForm.service_bonus_min_years} year minimum)</>
                )} gets <span className="font-medium text-foreground">
                  {!leaveForm.service_bonus_use_tiers 
                    ? Math.min(
                        Math.max(0, 5 - leaveForm.service_bonus_min_years) * leaveForm.service_bonus_days_per_year,
                        leaveForm.service_bonus_max_days
                      )
                    : calculateTieredBonus(5, leaveForm.service_bonus_tiers)
                  } extra days
                </span> 
                {' '}({leaveForm.default_annual_allowance} base + bonus = {' '}
                <span className="font-medium text-foreground">
                  {leaveForm.default_annual_allowance + (!leaveForm.service_bonus_use_tiers 
                    ? Math.min(
                        Math.max(0, 5 - leaveForm.service_bonus_min_years) * leaveForm.service_bonus_days_per_year,
                        leaveForm.service_bonus_max_days
                      )
                    : calculateTieredBonus(5, leaveForm.service_bonus_tiers)
                  )} total days
                </span>)
              </p>
            </div>
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-400">
                <strong>How it works:</strong> Bonus days are calculated at the start of your leave year 
                (1st of {options?.leave_year_months?.find(m => m.value === leaveForm.leave_year_start_month)?.name || 'January'})
                based on completed years of service at that date.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Public Holidays Section */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Public Holidays</h2>
            <p className="text-sm text-muted-foreground">Select which region's public holidays to use</p>
          </div>
        </div>
        
        <div>
          <Label>Public Holiday Region</Label>
          <select
            value={leaveForm.public_holiday_region}
            onChange={(e) => setLeaveForm({ ...leaveForm, public_holiday_region: e.target.value })}
            className="w-full sm:w-64 h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
            data-testid="public-holiday-region"
          >
            {options?.public_holiday_regions?.map((r) => (
              <option key={r.value} value={r.value}>{r.name}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">Bank holidays are automatically imported from gov.uk for all available years</p>
        </div>
        
        <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
          <p className="text-xs text-muted-foreground">
            <strong>Tip:</strong> If you have locations in different regions (e.g., Scotland), employees assigned 
            to those locations can use different bank holidays. Configure this in the Locations section.
          </p>
        </div>
      </div>

      {/* Reports → Getting a break (Rob, 12/10/2026: 5a) — who hasn't had time off lately. */}
      <div className="card-hive p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Coffee className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg">Getting a break</h2>
            <p className="text-sm text-muted-foreground">Reports → Getting a break flags anyone who hasn't had a day off for this long</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min="7"
            max="365"
            value={leaveForm.break_alert_days ?? 90}
            onChange={(e) => setLeaveForm({ ...leaveForm, break_alert_days: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
            className="input-hive w-24"
            data-testid="break-alert-days"
          />
          <span className="text-sm text-muted-foreground">days without a break (7 to 365)</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          A break is at least one full day of booked leave that isn't sickness. Weekends, bank holidays and working from home don't count.
        </p>
      </div>

      {/* Leave Calculator — real bank holidays + backend-served law (D9) */}
      <LeaveCalculator
        leaveSettings={leaveForm}
        bankHolidays={bankHolidays}
        jurisdiction={jurisdiction}
      />

      <div className="flex justify-end">
        <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-leave-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>
    </form>
    </>
  );

  // Helper function to calculate tiered bonus
  const calculateTieredBonus = (years, tiersJson) => {
    try {
      const tiers = JSON.parse(tiersJson || '[]');
      let totalBonus = 0;
      for (const tier of tiers) {
        if (years >= tier.after_years) {
          totalBonus += tier.extra_days;
        }
      }
      return totalBonus;
    } catch {
      return 0;
    }
  };

  // Service Bonus Tiers Component
  const ServiceBonusTiers = ({ tiers, onChange }) => {
    const parsedTiers = (() => {
      try {
        return JSON.parse(tiers || '[]');
      } catch {
        return [];
      }
    })();

    const addTier = () => {
      const newTiers = [...parsedTiers, { after_years: parsedTiers.length + 1, extra_days: 1 }];
      onChange(JSON.stringify(newTiers));
    };

    const updateTier = (index, field, value) => {
      const newTiers = [...parsedTiers];
      newTiers[index][field] = value;
      onChange(JSON.stringify(newTiers));
    };

    const removeTier = (index) => {
      const newTiers = parsedTiers.filter((_, i) => i !== index);
      onChange(JSON.stringify(newTiers));
    };

    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Define custom tiers for when employees reach certain milestones:
        </p>
        
        {parsedTiers.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <p>No tiers defined. Add your first tier below.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {parsedTiers.map((tier, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-background rounded-lg">
                <span className="text-sm text-muted-foreground whitespace-nowrap">After</span>
                <Input
                  type="number"
                  min="1"
                  value={tier.after_years}
                  onChange={(e) => updateTier(index, 'after_years', parseInt(e.target.value) || 1)}
                  className="w-20 h-8"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">year(s), add</span>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={tier.extra_days}
                  onChange={(e) => updateTier(index, 'extra_days', parseFloat(e.target.value) || 0)}
                  className="w-20 h-8"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">extra day(s)</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTier(index)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <Button type="button" variant="outline" size="sm" onClick={addTier}>
          <Plus className="w-4 h-4 mr-2" />
          Add Tier
        </Button>
      </div>
    );
  };

  // Render Leave Types Tab
  const renderLeaveTypesTab = () => (
    <div className="space-y-6">
      {/* Leave Types Header */}
      <div className="card-hive p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Tag className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg">Leave Types</h2>
              <p className="text-sm text-muted-foreground">Configure the types of leave employees can request</p>
            </div>
          </div>
          <Button onClick={() => openLeaveTypeDialog()} className="btn-primary" data-testid="add-leave-type-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Type
          </Button>
        </div>
        
        {/*
          The payroll setup nag. It exists because the migration GUESSED — every
          existing type was defaulted to "included" and "paid as normal", which is
          the safe direction but is still a guess. Labelling it as one and asking a
          human to confirm is honest; letting a guess quietly drive somebody's wages
          is not. It disappears type by type as each dialog is saved.
        */}
        {leaveTypes.filter(lt => lt.payroll_reviewed === false).length > 0 && (
          <div
            className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
            data-testid="payroll-setup-nag"
          >
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-none" />
            <p className="text-sm text-amber-600">
              <span className="font-semibold">
                {leaveTypes.filter(lt => lt.payroll_reviewed === false).length} leave
                {leaveTypes.filter(lt => lt.payroll_reviewed === false).length === 1 ? ' type hasn\'t' : ' types haven\'t'} been set up for payroll.
              </span>{' '}
              Until they are, we've assumed they're paid as normal. Open each one and check the payroll section.
            </p>
          </div>
        )}

        {leaveTypes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No leave types configured</p>
            <p className="text-sm mt-1">Add your first leave type to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaveTypes.map((lt) => {
              const IconComponent = getIconComponent(lt.icon);
              return (
                <div 
                  key={lt.id} 
                  className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg hover:bg-secondary/70 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${lt.color}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: lt.color }} />
                    </div>
                    <div>
                      <p className="font-medium">{lt.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {lt.deducted ? (
                          <span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-0.5 rounded">Deducts allowance</span>
                        ) : (
                          <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">No deduction</span>
                        )}
                        {/* H9: three ways a type is approved. "Auto-approved" used to mean
                            "never asks" — it now means something else, so it's gone. */}
                        {(() => {
                          const b = badgeFor(lt);
                          const tone = b.tone === 'auto'
                            ? 'bg-emerald-500/20 text-emerald-600'
                            : b.tone === 'recorded'
                              ? 'bg-indigo-500/15 text-indigo-500'
                              : 'bg-blue-500/20 text-blue-500';
                          return <span className={`text-xs px-2 py-0.5 rounded ${tone}`} data-testid="leave-type-approval-badge">{b.label}</span>;
                        })()}
                        {/*
                          ⚠️ THIS BADGE IS THE POINT. Sick Leave and Unpaid Leave both
                          show "No deduction" above and used to look identical — one is
                          normally paid, one is not, and that is the single most
                          pay-affecting fact on the whole payroll report.
                        */}
                        <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">
                          {lt.payroll_include === false
                            ? 'Not on payroll'
                            : `Payroll: ${payrollTreatmentLabel(lt.payroll_pay_treatment)}${lt.payroll_is_sickness ? ' · sickness' : ''}`}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openLeaveTypeDialog(lt)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteLeaveType(lt)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Leave Type Dialog */}
      {leaveTypeDialogOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg my-8">
            <div className="p-6 border-b border-border">
              <h3 className="font-heading font-bold text-lg">
                {editingLeaveType ? 'Edit Leave Type' : 'Add Leave Type'}
              </h3>
            </div>
            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Name */}
              <div>
                <Label>Name *</Label>
                <Input
                  value={leaveTypeForm.name}
                  onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, name: e.target.value })}
                  placeholder="e.g., Annual Leave, Sick Leave"
                  className="input-hive mt-1"
                  data-testid="leave-type-name-input"
                />
              </div>
              
              {/* Colour Picker */}
              <div>
                <Label>Colour</Label>
                <div className="grid grid-cols-10 gap-2 mt-2">
                  {COLOR_OPTIONS.map((color) => {
                    // Check if color is already used by another leave type (excluding current one being edited)
                    const isUsed = leaveTypes.some(lt => 
                      lt.color?.toLowerCase() === color.value.toLowerCase() && 
                      lt.id !== leaveTypeForm.id
                    );
                    const isSelected = leaveTypeForm.color === color.value;
                    
                    return (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => !isUsed && setLeaveTypeForm({ ...leaveTypeForm, color: color.value })}
                        className={`w-7 h-7 rounded-md transition-all relative ${
                          isSelected
                            ? 'ring-2 ring-offset-2 ring-offset-background ring-white scale-110' 
                            : isUsed 
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:scale-110'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={isUsed ? 'Colour already used by another leave type' : color.name}
                        disabled={isUsed}
                      >
                        {isUsed && (
                          <X className="w-5 h-5 text-white absolute inset-0 m-auto drop-shadow-md" strokeWidth={3} />
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Purple colours are reserved for Bank Holidays
                </p>
              </div>
              
              {/* Icon Picker */}
              <div>
                <Label>Icon</Label>
                <div className="mt-2 space-y-3">
                  {ICON_CATEGORIES.map((category) => (
                    <div key={category}>
                      <p className="text-xs text-muted-foreground mb-2">{category}</p>
                      <div className="flex flex-wrap gap-1">
                        {ICON_OPTIONS.filter(i => i.category === category).map((iconOpt) => {
                          const Icon = iconOpt.icon;
                          return (
                            <button
                              key={iconOpt.value}
                              type="button"
                              onClick={() => setLeaveTypeForm({ ...leaveTypeForm, icon: iconOpt.value })}
                              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                                leaveTypeForm.icon === iconOpt.value 
                                  ? 'ring-2 ring-primary bg-primary/20' 
                                  : 'bg-secondary/50 hover:bg-secondary'
                              }`}
                              title={iconOpt.name}
                            >
                              <Icon className="w-4 h-4" style={{ color: leaveTypeForm.color }} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Preview */}
              <div>
                <Label>Preview</Label>
                <div className="mt-2 p-4 bg-secondary/50 rounded-lg flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${leaveTypeForm.color}20` }}
                  >
                    {(() => {
                      const IconPreview = getIconComponent(leaveTypeForm.icon);
                      return <IconPreview className="w-5 h-5" style={{ color: leaveTypeForm.color }} />;
                    })()}
                  </div>
                  <span className="font-medium">{leaveTypeForm.name || 'Leave Type Name'}</span>
                </div>
              </div>
              
              {/* Annual Limit */}
              <div>
                <Label>Yearly limit (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={leaveTypeForm.limit_days}
                  onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, limit_days: e.target.value })}
                  placeholder="Leave blank for no limit"
                  className="input-hive mt-1"
                  data-testid="leave-type-yearly-limit"
                />
                <p className="text-xs text-muted-foreground mt-1">The most anyone can have in a leave year. Going over sends the request to their approver as an exception.</p>
              </div>
              
              {/* Checkboxes */}
              <div className="space-y-3 pt-2">
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={leaveTypeForm.deducted}
                    onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, deducted: e.target.checked })}
                    className="w-4 h-4 rounded border-input accent-primary"
                  />
                  <div>
                    <p className="font-medium text-sm">Deducts from allowance</p>
                    <p className="text-xs text-muted-foreground">Reduces the employee's annual leave balance</p>
                  </div>
                </label>
                
                <CountsAndPrivacy form={leaveTypeForm} setForm={setLeaveTypeForm} />
              </div>

              {/* "Requires approval" became three choices (H9). */}
              <ApprovalChoice
                form={leaveTypeForm}
                setForm={setLeaveTypeForm}
                limits={limits}
                rules={leaveForm}
                managers={managerSettingsForm}
                blackouts={blackouts}
              />

              {/*
                PAYROLL (DESIGN-payroll.md decision B).
                ⚠️ Nested on purpose. The three settings below are meaningless when
                "Include on payroll reports" is off, and shown flat this dialog grows
                to seven tick boxes in one column. Work From Home is the case: one
                tick, nothing else appears, because nothing else applies.
              */}
              <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600 mb-1">Payroll</p>

                <label className="flex items-start gap-3 cursor-pointer py-2">
                  <input
                    type="checkbox"
                    checked={leaveTypeForm.payroll_include}
                    onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, payroll_include: e.target.checked })}
                    className="w-4 h-4 mt-0.5 rounded border-input accent-primary"
                    data-testid="payroll-include-checkbox"
                  />
                  <div>
                    <p className="font-medium text-sm">Include on payroll reports</p>
                    <p className="text-xs text-muted-foreground">
                      Turn off for things that aren't really absence, like working from home
                    </p>
                  </div>
                </label>

                {leaveTypeForm.payroll_include && (
                  <div className="ml-7 pl-4 border-l-2 border-amber-500/30 space-y-1">
                    <div className="py-2">
                      <p className="font-medium text-sm mb-1">How is it paid?</p>
                      <select
                        value={leaveTypeForm.payroll_pay_treatment}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, payroll_pay_treatment: e.target.value })}
                        className="input-hive"
                        data-testid="payroll-pay-treatment-select"
                      >
                        <option value="paid">Paid as normal</option>
                        <option value="unpaid">Unpaid — payroll deducts</option>
                        <option value="statutory">Statutory — payroll decides</option>
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        "Statutory" tells payroll this one needs their judgement — SSP, SMP and the like.
                        HolidayHive never works out the money.
                      </p>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer py-2">
                      <input
                        type="checkbox"
                        checked={leaveTypeForm.payroll_is_sickness}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, payroll_is_sickness: e.target.checked })}
                        className="w-4 h-4 mt-0.5 rounded border-input accent-primary"
                        data-testid="payroll-sickness-checkbox"
                      />
                      <div>
                        <p className="font-medium text-sm">This is sickness absence</p>
                        <p className="text-xs text-muted-foreground">
                          Separate from whether it's paid — unpaid sick leave is a real thing. Reasons never leave the system either way.
                        </p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer py-2">
                      <input
                        type="checkbox"
                        checked={leaveTypeForm.payroll_allow_override}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, payroll_allow_override: e.target.checked })}
                        className="w-4 h-4 mt-0.5 rounded border-input accent-primary"
                        data-testid="payroll-override-checkbox"
                      />
                      <div>
                        <p className="font-medium text-sm">Managers can mark individual absences paid or unpaid</p>
                        <p className="text-xs text-muted-foreground">
                          For the one-off call — "we'll pay this one"
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-3">
              <Button variant="outline" onClick={() => setLeaveTypeDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={handleSaveLeaveType} 
                disabled={saving}
                data-testid="save-leave-type-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingLeaveType ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Blackout Periods Tab
  const renderBlackoutsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-hive p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Ban className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg">No-leave dates</h2>
              <p className="text-sm text-muted-foreground">
                Block dates when employees cannot request leave
              </p>
            </div>
          </div>
          <Button onClick={() => openBlackoutDialog()} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add no-leave dates
          </Button>
        </div>
        
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm text-amber-400">
            <strong>How it works:</strong> When employees try to book leave that overlaps with no-leave dates, 
            they will receive an error message and the request will be blocked. Use this for busy periods, 
            month-end closes, or critical business dates.
          </p>
        </div>
      </div>
      
      {/* Blackout List */}
      <div className="card-hive p-6">
        {blackouts.length === 0 ? (
          <div className="text-center py-12">
            <Ban className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-lg mb-1">No no-leave dates yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add your first no-leave dates to block specific dates
            </p>
            <Button onClick={() => openBlackoutDialog()} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add no-leave dates
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {blackouts.map((blackout) => (
              <div 
                key={blackout.id} 
                className={`p-4 rounded-lg border transition-colors ${
                  blackout.is_active 
                    ? 'border-border bg-secondary/30' 
                    : 'border-border/50 bg-secondary/10 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      blackout.is_active ? 'bg-red-500/20' : 'bg-muted'
                    }`}>
                      <Ban className={`w-5 h-5 ${blackout.is_active ? 'text-red-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{blackout.name}</h3>
                        {blackout.is_recurring && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400">
                            Recurring
                          </span>
                        )}
                        {!blackout.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span>
                          {new Date(blackout.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' '}&ndash;{' '}
                          {new Date(blackout.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="text-muted-foreground/50">|</span>
                        <span className="capitalize">
                          {blackout.scope === 'company' && 'Company-wide'}
                          {blackout.scope === 'department' && `Dept: ${blackout.department_name || 'Unknown'}`}
                          {blackout.scope === 'user' && `User: ${blackout.user_name || 'Unknown'}`}
                        </span>
                      </div>
                      {blackout.reason && (
                        <p className="text-xs text-muted-foreground mt-1">{blackout.reason}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleToggleBlackoutActive(blackout)}
                      title={blackout.is_active ? 'Disable' : 'Enable'}
                    >
                      {blackout.is_active ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openBlackoutDialog(blackout)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteBlackout(blackout)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Blackout Dialog */}
      {blackoutDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading font-bold text-lg mb-4">
              {editingBlackout ? 'Edit no-leave dates' : 'Add no-leave dates'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={blackoutForm.name}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, name: e.target.value })}
                  placeholder="e.g., Christmas Shutdown, Month-End Close"
                  className="input-hive mt-1"
                />
              </div>
              
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={blackoutForm.reason}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, reason: e.target.value })}
                  placeholder="e.g., All staff required for stocktake"
                  className="input-hive mt-1"
                />
              </div>
              
              {/* ONE pop-up for both days (DateRangeField, Rob 3A 02/11/2026). The form keeps its
                  yyyy-mm-dd strings; the field works in days. */}
              <DateRangeField
                startLabel="Start date *"
                endLabel="End date *"
                start={blackoutForm.start_date ? parseISO(blackoutForm.start_date) : null}
                end={blackoutForm.end_date ? parseISO(blackoutForm.end_date) : null}
                onChange={({ start, end }) => setBlackoutForm({ ...blackoutForm, start_date: start ? format(start, 'yyyy-MM-dd') : '', end_date: end ? format(end, 'yyyy-MM-dd') : '' })}
              />
              
              <div>
                <Label>Applies To</Label>
                <select
                  value={blackoutForm.scope}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, scope: e.target.value })}
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                >
                  <option value="company">Everyone (Company-wide)</option>
                  <option value="department">Specific Department</option>
                  <option value="user">Specific Employee</option>
                </select>
              </div>
              
              {blackoutForm.scope === 'department' && (
                <div>
                  <Label>Department *</Label>
                  <select
                    value={blackoutForm.department_id}
                    onChange={(e) => setBlackoutForm({ ...blackoutForm, department_id: e.target.value })}
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select department...</option>
                    {blackoutDeptOptions.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {blackoutForm.scope === 'user' && (
                <div>
                  <Label>Employee *</Label>
                  <select
                    value={blackoutForm.user_id}
                    onChange={(e) => setBlackoutForm({ ...blackoutForm, user_id: e.target.value })}
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select employee...</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                <input
                  type="checkbox"
                  checked={blackoutForm.is_recurring}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, is_recurring: e.target.checked })}
                  className="w-5 h-5 rounded border-input accent-primary"
                />
                <div>
                  <p className="font-medium">Recurring annually</p>
                  <p className="text-xs text-muted-foreground">These dates will repeat every year</p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="secondary" 
                className="flex-1"
                onClick={() => setBlackoutDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="btn-primary flex-1" 
                onClick={handleSaveBlackout}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingBlackout ? 'Save changes' : 'Add no-leave dates'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Working Hours Tab
  const renderWorkingHoursTab = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    return (
      <div className="space-y-6">
        <div className="card-hive p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold">Business Hours</h2>
              <p className="text-sm text-muted-foreground">
                Set your company's standard working hours. This will be the default for new employees.
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Weekly hours are a RESULT of the schedule, not a competing claim (D10).
                The old free-typed "Default Hours per Week" input let the declared hours
                and the actual pattern contradict each other — the audit found the same
                full-timer reading as 40h, 37.5h and 35h at once. */}
            <div className="mb-6">
              <Label>Hours per Week</Label>
              <p className="font-heading text-2xl font-bold mt-1">
                {(() => { const w = computeWeeklyHours(businessHours, null); return Number.isInteger(w) ? w : w.toFixed(1); })()} hrs
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Worked out from the schedule below — change the times or the break to change it.
              </p>
            </div>

            <WorkingHoursEditor
              hours={businessHours}
              companyHours={null}
              onChange={(j) => setBusinessHours(JSON.parse(j))}
              warnings={hoursWarnings}
              defaultBreakMinutes={defaultBreakMinutes}
              onBreakMinutesChange={setDefaultBreakMinutes}
            />
          </div>
          
          <div className="flex justify-end mt-6 pt-6 border-t border-border">
            <Button 
              onClick={handleSaveBusinessHours} 
              className="btn-primary"
              disabled={saving}
              data-testid="save-business-hours-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Business Hours
            </Button>
          </div>
        </div>
        
        <div className="card-hive p-6">
          <h3 className="font-heading font-bold mb-3">About Working Hours</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• These hours define your company's standard operating schedule</li>
            <li>• New employees will inherit these hours by default</li>
            <li>• Individual employee hours can be customized in their profile</li>
            <li>• Part-time employees can have reduced schedules set individually</li>
            <li>• An unpaid break is the gap between the morning and afternoon sessions and isn't counted in weekly hours. A paid break is working time — no gap, still counted — and still satisfies the statutory rest-break minimum</li>
            <li>• Weekly hours are worked out from the schedule, so the two always agree</li>
          </ul>
        </div>
      </div>
    );
  };

  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  // Manager permission settings definitions
  const managerPermissions = [
    {
      key: 'mgr_can_view_team_balances',
      label: 'View Team Leave Balances',
      description: 'Managers can see their team\'s remaining leave on the Team calendar and Employees page. Whoever approves a request always sees the person\'s balance when they come to approve it.',
      icon: <Eye className="w-5 h-5" />
    },
    {
      key: 'mgr_can_approve_leave',
      label: 'Approve/Decline Leave Requests',
      description: 'Managers can approve or decline leave requests from their team',
      icon: <CheckSquare className="w-5 h-5" />
    },
    {
      key: 'mgr_can_view_leave_reasons',
      label: 'View Leave Reasons',
      description: 'Managers can see the reason provided for leave requests',
      icon: <FileText className="w-5 h-5" />
    },
    {
      key: 'mgr_can_edit_team_details',
      label: 'Edit Team Member Details',
      description: 'Managers can edit their team members\' employee information',
      icon: <Pencil className="w-5 h-5" />
    },
    {
      key: 'mgr_can_view_personal_info',
      label: 'View Personal Information',
      description: 'Managers can see personal details like phone, date of birth, and emergency contacts',
      icon: <Shield className="w-5 h-5" />
    },
    {
      key: 'mgr_can_cancel_approved_leave',
      label: 'Cancel Approved Leave',
      description: 'Managers can cancel leave that has already been approved for their team',
      icon: <XCircle className="w-5 h-5" />
    },
    {
      key: 'mgr_can_book_team_leave',
      label: 'Book Leave for Team',
      description: 'Managers can book or record leave for their team from the team calendar (e.g. mark someone off sick). Booked leave is confirmed immediately.',
      icon: <CalendarCheck className="w-5 h-5" />
    },
    {
      key: 'mgr_can_approve_own_leave',
      label: 'Approve Own Leave',
      description: 'When a manager is their own approver, let them approve their own leave instead of it escalating to a backup or admin.',
      icon: <UserCheck className="w-5 h-5" />
    },
    {
      key: 'mgr_receive_notifications',
      label: 'Receive email notifications',
      // Says what turning it OFF actually costs you. Without that it reads as a
      // pointless setting ("how would they know a request arrived?"), and it hides
      // the fact that the reminder ladder deliberately ignores it — chasing an
      // overdue request isn't a notification, it's a safety net.
      description: "Email managers when someone on their team requests leave. With this off they'll only see new requests by logging in — overdue reminders and escalations are always sent",
      icon: <Mail className="w-5 h-5" />
    },
    {
      key: 'mgr_can_create_blackouts',
      label: 'Add no-leave dates',
      description: 'Managers can add no-leave dates to block leave requests for their department',
      icon: <Ban className="w-5 h-5" />
    },
    {
      // DESIGN-reports.md C and D (23/09/2026): off by default; how far a manager sees
      // is the reach set on their profile, not a second setting here.
      key: 'mgr_can_view_reports',
      label: 'View reports',
      description: 'Managers can open Reports. They only see the people inside their reach, which you set on each manager\'s profile.',
      icon: <BarChart3 className="w-5 h-5" />
    },
    {
      // DESIGN-reports.md E (Rob, 12/10/2026: 1a). Health data, so its own switch, off by
      // default, and it only works with "View reports" on as well.
      key: 'mgr_can_view_sickness',
      label: 'View sickness',
      description: 'Managers can open Reports → Sickness for the people inside their reach. It\'s health information, so leave it off unless they need it. Needs "View reports" on too.',
      icon: <HeartPulse className="w-5 h-5" />
    }
  ];

  // Render Manager Permissions Tab
  const renderManagerPermissionsTab = () => (
    <div className="space-y-6">
      <form onSubmit={handleSaveManagerSettings}>
        <div className="flex flex-col xl:flex-row gap-6">
          <div className="w-full xl:flex-1 space-y-6">
        {/* Manager Permissions Card */}
        <div className="card-hive p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <UserCog className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h3 className="font-semibold">Global Manager Permissions</h3>
              <p className="text-sm text-muted-foreground">Default permissions for all managers. These can be overridden per manager in their employee profile.</p>
            </div>
          </div>

          <div className="space-y-4">
            {managerPermissions.map((permission) => (
              <div 
                key={permission.key}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    managerSettingsForm[permission.key] 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {permission.icon}
                  </div>
                  <div>
                    <p className="font-medium">{permission.label}</p>
                    <p className="text-sm text-muted-foreground">{permission.description}</p>
                  </div>
                </div>
                <Switch
                  checked={managerSettingsForm[permission.key]}
                  onCheckedChange={(checked) => setManagerSettingsForm(prev => ({
                    ...prev,
                    [permission.key]: checked
                  }))}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Maximum Approval Days Card */}
        <div className="card-hive p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <CalendarClock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-semibold">Approval Limits</h3>
              <p className="text-sm text-muted-foreground">Set limits on what managers can approve without escalation</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 rounded-lg border border-border">
              <Label htmlFor="mgr_max_approval_days" className="text-sm font-medium">
                Maximum Consecutive Days
              </Label>
              <p className="text-sm text-muted-foreground mb-3">
                Maximum number of consecutive days a manager can approve. Set to 0 for unlimited. Requests exceeding this limit will require admin approval.
              </p>
              <div className="flex items-center gap-4">
                <Input
                  id="mgr_max_approval_days"
                  type="number"
                  min="0"
                  max="365"
                  value={managerSettingsForm.mgr_max_approval_days}
                  onChange={(e) => setManagerSettingsForm(prev => ({
                    ...prev,
                    mgr_max_approval_days: parseInt(e.target.value) || 0
                  }))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  {managerSettingsForm.mgr_max_approval_days === 0 
                    ? 'Unlimited' 
                    : `days (requests over ${managerSettingsForm.mgr_max_approval_days} days need admin approval)`}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={managerSettingsLoading}>
            {managerSettingsLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Manager Settings
              </>
            )}
          </Button>
        </div>
          </div>
          <div className="w-full xl:w-[42rem] xl:shrink-0 xl:sticky xl:top-40 xl:self-start">
            <div>
        {/* Managers overview */}
        <div className="card-hive p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">Managers</h3>
              <p className="text-sm text-muted-foreground">Who follows the policy above, who's custom, and how far each one reaches.</p>
            </div>
            {mgrList.some(isCustomMgr) && (
              <Button type="button" variant="outline" onClick={() => setResetConfirm({ open: true, ids: mgrList.filter(isCustomMgr).map((m) => m.id), name: '' })}>Reset all to global</Button>
            )}
          </div>
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
              <thead className="bg-secondary/40 text-left">
                <tr>
                  <th className="p-2.5 font-medium">Manager</th>
                  <th className="p-2.5 font-medium">View reach</th>
                  <th className="p-2.5 font-medium">Manage reach</th>
                  <th className="p-2.5 font-medium">Capabilities</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {mgrList.map((m) => (
                  <tr key={m.id} className="border-t border-border/50">
                    <td className="p-2.5 font-medium">{m.first_name} {m.last_name}</td>
                    <td className="p-2.5 text-muted-foreground">{reachLabel(m.manager_view_scope)}</td>
                    <td className="p-2.5 text-muted-foreground">{reachLabel(m.manager_manage_scope)}</td>
                    <td className="p-2.5">
                      {isCustomMgr(m)
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600">Custom</span>
                        : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">Follows global</span>}
                    </td>
                    <td className="p-2.5 text-right">
                      {isCustomMgr(m) && <Button type="button" variant="ghost" onClick={() => setResetConfirm({ open: true, ids: [m.id], name: `${m.first_name} ${m.last_name}` })}>Reset</Button>}
                    </td>
                  </tr>
                ))}
                {mgrList.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No managers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
            </div>
          </div>
        </div>
      </form>

      {applyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setApplyOpen(false)}>
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-1">Apply this change to custom managers?</h3>
            <p className="text-sm text-muted-foreground mb-3">These managers use custom permissions, so this change won't reach them automatically. Tick anyone you'd like to apply just this change to — their other custom settings stay as they are.</p>
            <div className="flex gap-2 mb-2">
              <Button type="button" variant="outline" onClick={() => setApplyIds(mgrList.filter(isCustomMgr).map((m) => m.id))}>Select all</Button>
              <Button type="button" variant="outline" onClick={() => setApplyIds([])}>Deselect all</Button>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1 border border-border rounded-lg p-2 mb-4">
              {mgrList.filter(isCustomMgr).map((m) => (
                <label key={m.id} className="flex items-center gap-2 p-1.5 hover:bg-secondary/40 rounded cursor-pointer">
                  <input type="checkbox" checked={applyIds.includes(m.id)} onChange={(e) => setApplyIds(e.target.checked ? [...applyIds, m.id] : applyIds.filter((x) => x !== m.id))} />
                  <span className="text-sm">{m.first_name} {m.last_name}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setApplyOpen(false)}>Not now</Button>
              <Button type="button" className="btn-primary" disabled={applyIds.length === 0} onClick={async () => { await applyChangesToManagers(applyIds, applyChanges); setApplyOpen(false); }}>Apply to selected ({applyIds.length})</Button>
            </div>
          </div>
        </div>
      )}

      {resetConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setResetConfirm({ open: false, ids: [], name: '' })}>
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-2">Turn off custom settings?</h3>
            <p className="text-sm text-muted-foreground mb-3">
              {resetConfirm.name
                ? <>Are you sure you want to turn off <span className="text-foreground font-medium">{resetConfirm.name}</span>&rsquo;s custom managerial settings? They&rsquo;ll follow the global manager policy from now on.</>
                : <>Are you sure you want to turn off custom settings for these <span className="text-foreground font-medium">{resetConfirm.ids.length}</span> managers? They&rsquo;ll follow the global manager policy from now on.</>}
            </p>
            <div className="text-xs text-muted-foreground bg-secondary/40 border border-border/60 rounded-lg p-3 mb-4">
              To give {resetConfirm.name ? 'them' : 'a manager'} custom settings again later, go to <span className="text-foreground">Employees &rarr; the person &rarr; Manager Permissions</span> and switch off &ldquo;Use global manager settings&rdquo;.
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setResetConfirm({ open: false, ids: [], name: '' })}>Cancel</Button>
              <Button type="button" className="btn-primary" onClick={async () => { const ids = resetConfirm.ids; setResetConfirm({ open: false, ids: [], name: '' }); await resetManagersToGlobal(ids); }}>Turn off custom settings</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Notifications Tab
  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <form onSubmit={handleSaveNotifications} className="space-y-6">
        {/* Global Timezone Setting */}
        <div className="card-hive p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">When scheduled emails go out</h3>
              <p className="text-sm text-muted-foreground">On your company's clock — the same one the year-end rollover uses</p>
            </div>
          </div>

          {/* ⚠️ NO SECOND TIMEZONE BOX (A9, Rob 21/10/2026). There used to be one here
              with its own hand-typed list, and it could disagree with General's. One
              company timezone now; this only says which, and where to change it. */}
          <p className="text-sm rounded-lg bg-secondary/60 border border-border px-3 py-2.5" data-testid="email-timezone-pointer">
            Emails go out on your company's time:{' '}
            <strong>{(options?.timezones || []).find((tz) => tz.value === generalForm.timezone)?.label?.replace(/ \(UTC[^)]*\)$/, '') || generalForm.timezone}</strong>.{' '}
            <button type="button" className="underline text-primary" onClick={() => setActiveTab('general')}>Change it in General</button>
          </p>

          {/*
            * ⚠️ **THE TEAMS CHANNEL SITS BESIDE THE TIMEZONE BECAUSE IT IS THE SAME KIND
            * OF THING** — one value shared by all three digests. Three per-digest channels
            * would be three chances to leave one unset, and an unset channel posts nothing
            * while its switch reads "On". Rob's decision, 24/08/2026.
            */}
          <div className="mt-6 pt-6 border-t border-border">
            <TeamsChannelPicker
              channels={teamsChannels}
              value={notificationsForm.teams_channel_id}
              onChange={(v) => setNotificationsForm({ ...notificationsForm, teams_channel_id: v })}
            />
          </div>
          {/*
            * Slack build step 4 — Rob's A1: the Slack channel sits here too, under the Teams
            * one, for the same reason. Nothing at all shows for a company without Slack.
            */}
          {slackChannels?.connected && (
            <div className="mt-6 pt-6 border-t border-border">
              <SlackChannelPicker
                slack={slackChannels}
                value={notificationsForm.slack_channel_id}
                onChange={(v) => setNotificationsForm({ ...notificationsForm, slack_channel_id: v })}
                onRefresh={refreshSlackChannels}
                refreshing={refreshingSlackChannels}
              />
            </div>
          )}
        </div>

        {/* Daily Digest */}
        <div className="card-hive p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Sun className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Daily Digest</h3>
                <p className="text-sm text-muted-foreground">Email summary of who's off today</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationsForm.daily_digest_enabled}
                onChange={(e) => setNotificationsForm({...notificationsForm, daily_digest_enabled: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
          
          {notificationsForm.daily_digest_enabled && (
            <div className="space-y-4 pt-4 border-t border-border">
              <div>
                <Label className="text-sm font-medium mb-2 block">Send at</Label>
                <input
                  type="time"
                  value={notificationsForm.daily_digest_time}
                  onChange={(e) => setNotificationsForm({...notificationsForm, daily_digest_time: e.target.value})}
                  className="p-3 rounded-lg bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Who should receive this by email?</Label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>Admins</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.daily_digest_notify_admins}
                        onChange={(e) => setNotificationsForm({...notificationsForm, daily_digest_notify_admins: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>Managers</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.daily_digest_notify_managers}
                        onChange={(e) => setNotificationsForm({...notificationsForm, daily_digest_notify_managers: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>All Staff</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.daily_digest_notify_all_staff}
                        onChange={(e) => setNotificationsForm({...notificationsForm, daily_digest_notify_all_staff: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/*
            * ⚠️ OUTSIDE the `enabled &&` block above, on purpose. If it were inside, an
            * administrator who switched Teams on and the digest off would never see the
            * sentence telling them nothing will post — which is precisely the state that
            * needs explaining, and the reason independent switches (decision (a)) are safe.
            */}
          {teamsChannels !== null && (
            <DigestTeamsRow
              kind="daily"
              enabled={notificationsForm.daily_digest_to_teams}
              digestEnabled={notificationsForm.daily_digest_enabled}
              channelId={notificationsForm.teams_channel_id}
              channels={teamsChannels}
              onChange={(v) => setNotificationsForm({ ...notificationsForm, daily_digest_to_teams: v })}
              withTest={false}
            />
          )}
          <DigestSlackRow
            kind="daily"
            enabled={notificationsForm.daily_digest_to_slack}
            digestEnabled={notificationsForm.daily_digest_enabled}
            channelId={notificationsForm.slack_channel_id}
            slack={slackChannels}
            onChange={(v) => setNotificationsForm({ ...notificationsForm, daily_digest_to_slack: v })}
          />
          {teamsChannels !== null && (
            <div className={digestChatRowsShown ? '' : 'border-t border-border mt-4 pt-4'}>
              <SendTestDigest kind="daily" />
            </div>
          )}
        </div>

        {/* Weekly Digest */}
        <div className="card-hive p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Weekly Digest</h3>
                <p className="text-sm text-muted-foreground">Email summary of who's off this week</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationsForm.weekly_digest_enabled}
                onChange={(e) => setNotificationsForm({...notificationsForm, weekly_digest_enabled: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
            </label>
          </div>
          
          {notificationsForm.weekly_digest_enabled && (
            <div className="space-y-4 pt-4 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Send on</Label>
                  <select
                    value={notificationsForm.weekly_digest_day}
                    onChange={(e) => setNotificationsForm({...notificationsForm, weekly_digest_day: e.target.value})}
                    className="w-full p-3 rounded-lg bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {dayOptions.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">At time</Label>
                  <input
                    type="time"
                    value={notificationsForm.weekly_digest_time}
                    onChange={(e) => setNotificationsForm({...notificationsForm, weekly_digest_time: e.target.value})}
                    className="w-full p-3 rounded-lg bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Who should receive this by email?</Label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>Admins</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.weekly_digest_notify_admins}
                        onChange={(e) => setNotificationsForm({...notificationsForm, weekly_digest_notify_admins: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>Managers</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.weekly_digest_notify_managers}
                        onChange={(e) => setNotificationsForm({...notificationsForm, weekly_digest_notify_managers: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>All Staff</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.weekly_digest_notify_all_staff}
                        onChange={(e) => setNotificationsForm({...notificationsForm, weekly_digest_notify_all_staff: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          {teamsChannels !== null && (
            <DigestTeamsRow
              kind="weekly"
              enabled={notificationsForm.weekly_digest_to_teams}
              digestEnabled={notificationsForm.weekly_digest_enabled}
              channelId={notificationsForm.teams_channel_id}
              channels={teamsChannels}
              onChange={(v) => setNotificationsForm({ ...notificationsForm, weekly_digest_to_teams: v })}
              withTest={false}
            />
          )}
          <DigestSlackRow
            kind="weekly"
            enabled={notificationsForm.weekly_digest_to_slack}
            digestEnabled={notificationsForm.weekly_digest_enabled}
            channelId={notificationsForm.slack_channel_id}
            slack={slackChannels}
            onChange={(v) => setNotificationsForm({ ...notificationsForm, weekly_digest_to_slack: v })}
          />
          {teamsChannels !== null && (
            <div className={digestChatRowsShown ? '' : 'border-t border-border mt-4 pt-4'}>
              <SendTestDigest kind="weekly" />
            </div>
          )}
        </div>

        {/* Monthly Digest */}
        <div className="card-hive p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Monthly Digest</h3>
                <p className="text-sm text-muted-foreground">Email summary of who's off this month</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationsForm.monthly_digest_enabled}
                onChange={(e) => setNotificationsForm({...notificationsForm, monthly_digest_enabled: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
            </label>
          </div>
          
          {notificationsForm.monthly_digest_enabled && (
            <div className="space-y-4 pt-4 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Send on day</Label>
                  <select
                    value={notificationsForm.monthly_digest_day}
                    onChange={(e) => setNotificationsForm({...notificationsForm, monthly_digest_day: parseInt(e.target.value)})}
                    className="w-full p-3 rounded-lg bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {[...Array(28)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of month</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">At time</Label>
                  <input
                    type="time"
                    value={notificationsForm.monthly_digest_time}
                    onChange={(e) => setNotificationsForm({...notificationsForm, monthly_digest_time: e.target.value})}
                    className="w-full p-3 rounded-lg bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Who should receive this by email?</Label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>Admins</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.monthly_digest_notify_admins}
                        onChange={(e) => setNotificationsForm({...notificationsForm, monthly_digest_notify_admins: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>Managers</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.monthly_digest_notify_managers}
                        onChange={(e) => setNotificationsForm({...notificationsForm, monthly_digest_notify_managers: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                    <span>All Staff</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationsForm.monthly_digest_notify_all_staff}
                        onChange={(e) => setNotificationsForm({...notificationsForm, monthly_digest_notify_all_staff: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          {teamsChannels !== null && (
            <DigestTeamsRow
              kind="monthly"
              enabled={notificationsForm.monthly_digest_to_teams}
              digestEnabled={notificationsForm.monthly_digest_enabled}
              channelId={notificationsForm.teams_channel_id}
              channels={teamsChannels}
              onChange={(v) => setNotificationsForm({ ...notificationsForm, monthly_digest_to_teams: v })}
              withTest={false}
            />
          )}
          <DigestSlackRow
            kind="monthly"
            enabled={notificationsForm.monthly_digest_to_slack}
            digestEnabled={notificationsForm.monthly_digest_enabled}
            channelId={notificationsForm.slack_channel_id}
            slack={slackChannels}
            onChange={(v) => setNotificationsForm({ ...notificationsForm, monthly_digest_to_slack: v })}
          />
          {teamsChannels !== null && (
            <div className={digestChatRowsShown ? '' : 'border-t border-border mt-4 pt-4'}>
              <SendTestDigest kind="monthly" />
            </div>
          )}
        </div>

        {/* Birthday Reminders Link */}
        <div className="card-hive p-6 bg-pink-500/5 border-pink-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <Cake className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Birthday Reminders</h3>
                <p className="text-sm text-muted-foreground">Configure who gets notified about upcoming birthdays</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSearchParams({ tab: 'birthdays' })}
              className="border-pink-500/50 text-pink-500 hover:bg-pink-500/10"
            >
              Configure
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-notifications-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Notification Settings
          </Button>
        </div>
      </form>
    </div>
  );

  // Render Birthdays Tab
  const renderBirthdaysTab = () => (
    <div className="space-y-6">
      <form onSubmit={handleSaveBirthday} className="space-y-6">
        {/* Birthday Reminders Card */}
        <div className="card-hive p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Cake className="w-5 h-5 text-pink-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Birthday Reminders</h3>
              <p className="text-sm text-muted-foreground">Configure who receives birthday notifications</p>
            </div>
          </div>
          
          {/* Master Toggle */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg mb-6">
            <div>
              <Label className="font-medium">Enable Birthday Reminders</Label>
              <p className="text-sm text-muted-foreground">Send email reminders about upcoming birthdays</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={birthdayForm.birthday_reminders_enabled}
                onChange={(e) => setBirthdayForm({...birthdayForm, birthday_reminders_enabled: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
            </label>
          </div>
          
          {birthdayForm.birthday_reminders_enabled && (
            <div className="space-y-4">
              <div className="bg-secondary/20 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Email reminders will be sent <strong>1 week before</strong> and <strong>2 days before</strong> each employee's birthday.
                    The birthday person is never included in the notification list.
                  </p>
                </div>
              </div>
              
              <p className="text-sm font-medium text-muted-foreground mb-3">Who should receive notifications?</p>
              
              {/* All Staff */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                <div>
                  <Label className="font-medium">All Staff Members</Label>
                  <p className="text-sm text-muted-foreground">Everyone in the company (except the birthday person)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={birthdayForm.birthday_notify_all_staff}
                    onChange={(e) => setBirthdayForm({...birthdayForm, birthday_notify_all_staff: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              {/* All Admins */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                <div>
                  <Label className="font-medium">All Admin Members</Label>
                  <p className="text-sm text-muted-foreground">All users with Admin role (except the birthday person)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={birthdayForm.birthday_notify_admins}
                    onChange={(e) => setBirthdayForm({...birthdayForm, birthday_notify_admins: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              {/* Direct Manager */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                <div>
                  <Label className="font-medium">Direct Manager</Label>
                  <p className="text-sm text-muted-foreground">The birthday person's department manager</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={birthdayForm.birthday_notify_manager}
                    onChange={(e) => setBirthdayForm({...birthdayForm, birthday_notify_manager: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              {/* Department Members */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary/20 transition-colors">
                <div>
                  <Label className="font-medium">Department Members</Label>
                  <p className="text-sm text-muted-foreground">Everyone in the same department (except the birthday person)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={birthdayForm.birthday_notify_department}
                    onChange={(e) => setBirthdayForm({...birthdayForm, birthday_notify_department: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-amber-500">Note:</strong> If an employee falls into multiple notification groups, they will only receive one email (deduplicated).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Wallchart Display Info */}
        <div className="card-hive p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Wallchart Display</h3>
              <p className="text-sm text-muted-foreground">Birthdays are shown on the team calendar</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-lg">
            <span className="text-2xl">🥳</span>
            <p className="text-sm text-muted-foreground">
              Birthdays appear on the team calendar with a party emoji on the employee's row. 
              Make sure employees have their <strong>Date of Birth</strong> set in their profile.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-birthday-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Birthday Settings
          </Button>
        </div>
      </form>
    </div>
  );

  // Render Billing Tab
  const handleReinstate = async () => {
    setReinstating(true);
    try {
      const r = await billingApi.reinstateAccount();
      toast.success(r.data?.message || 'Your account is back.');
      setCloseStatus({ closing: false });
      window.dispatchEvent(new Event(ACCOUNT_STATUS_CHANGED));   // tell the banner
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reinstate the account');
    } finally {
      setReinstating(false);
    }
  };

  // Keep the Settings "Close account" section in sync when the state changes
  // elsewhere (e.g. the floating banner's "Keep our account"). Fixes the stale
  // card Rob saw: green toast, but the section didn't revert until a refresh.
  // Also re-read the billing overview: the "set to cancel" / "next payment" bar
  // at the top of the tab reads cancel_at_period_end from it, and a cancel or
  // undo flips that on Stripe before the request returns. Without this the bar
  // stayed green until a refresh (Rob, 22/09/2026).
  useEffect(() => {
    const resync = () => {
      billingApi.accountStatus()
        .then((r) => setCloseStatus(r.data)).catch(() => {});
      billingApi.getOverview()
        .then((r) => { if (r.data) setBilling(r.data); }).catch(() => {});
    };
    window.addEventListener(ACCOUNT_STATUS_CHANGED, resync);
    return () => window.removeEventListener(ACCOUNT_STATUS_CHANGED, resync);
  }, []);

  const downloadOwnData = async () => {
    try {
      const r = await billingApi.exportOwnData();
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(billing?.company_name || settings?.company_name || 'company').replace(/\s+/g, '-')}-full-backup.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export your data');
    }
  };

  // The call-to-action inside a plan card (monthly / annual). Trial customers may
  // switch plan AND payment method freely — each switch is a fresh checkout that
  // supersedes the old trial subscription (see billing.py), so their current combo
  // is marked and every OTHER combo is an offered switch. Paying customers change
  // things via the Portal; brand-new tenants see the initial pay buttons.
  // ── IS THERE ACTUALLY A LIVE SUBSCRIPTION? ─────────────────────────────────
  //
  // The whole billing tab used to answer this with `has_payment_method`, which is a
  // different question. A card stays on file after a subscription is cancelled — so a
  // cancelled customer was shown a green "your subscription is active" banner AND the
  // paying-customer plan buttons, with **no way to start paying again**. They were
  // locked out by the paywall and sent to a page that told them everything was fine.
  //
  // Rob hit exactly that on 31/07/2026 and couldn't find a route out as the customer.
  // The realistic version isn't a cancellation though — it's a card that fails, Stripe
  // marking the subscription past_due or unpaid, and the customer seeing a green tick.
  //
  // `subscription_status` is Stripe's own status. Null (a GoCardless customer, or an
  // older row) is treated as live so nobody currently paying is suddenly told they
  // aren't — this narrows a false "active", it doesn't widen a false "inactive".
  const DEAD_SUB_STATUSES = ['canceled', 'cancelled', 'unpaid', 'incomplete_expired', 'incomplete'];
  const subStatus = (billing?.subscription_status || '').toLowerCase();
  const subscriptionDead = DEAD_SUB_STATUSES.includes(subStatus);
  const subscriptionPastDue = subStatus === 'past_due';
  // "They are a paying customer right now" — the test the rest of this tab should use.
  const subscriptionLive = billing?.has_payment_method && !subscriptionDead;

  const renderPlanCta = (plan) => {
    // "On trial" = payment set up, but the trial hasn't ended yet. Stripe reports
    // status 'trialing'; GoCardless subscriptions report 'active' even while the
    // first collection is deferred to trial-end — so we also treat a future
    // trial_ends_at as on-trial, or a GC customer would wrongly see the paying
    // "Manage billing" button (which has no Stripe portal to open).
    const trialEndsInFuture = billing?.trial_ends_at && new Date(billing.trial_ends_at) > new Date();
    // `subscriptionLive`, not `has_payment_method` — third instance of the same fault.
    // Golamb's subscription was cancelled while `trial_ends_at` still sat in the
    // future, so this said "on trial", took the trial location, found no payment
    // providers to offer a switch to, and rendered an EMPTY BOX. That's how a locked-out
    // customer ended up on a page with literally no button to press. A cancelled
    // subscription is not a trial, whatever the dates say.
    const onTrial = subscriptionLive
      && (billing?.subscription_status === 'trialing' || trialEndsInFuture);
    const curCard = billing?.plan_type === plan && billing?.payment_channel === 'card';
    const curDD = billing?.plan_type === plan
      && (billing?.payment_channel === 'bacs' || billing?.payment_method === 'gocardless');
    // Name the destination PLAN in switch buttons — when they're already paying this
    // way, the change is the plan, not the method — and nudge the annual saving.
    const planWord = plan === 'annual' ? 'Annual' : 'Monthly';
    const saveTag = plan === 'annual' ? ' and Save!' : '';

    if (onTrial) {
      return (
        <div className="space-y-2">
          {billing?.card_available && (curCard ? (
            <div className="flex items-center gap-2 text-primary text-sm"><Check className="w-4 h-4" /> Current — Card</div>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => handleStripeCheckout(plan)}>
              <CardIcon className="w-4 h-4 mr-2" /> Switch to {planWord} Card{saveTag}
            </Button>
          ))}
          {billing?.dd_available && (curDD ? (
            <div className="flex items-center gap-2 text-primary text-sm"><Check className="w-4 h-4" /> Current — Direct Debit</div>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => handleDirectDebit(plan)}>
              <Banknote className="w-4 h-4 mr-2" /> Switch to {planWord} Direct Debit{saveTag}
            </Button>
          ))}
        </div>
      );
    }

    // Paying (post-trial) customer. A plan switch here is SCHEDULED for the current
    // period end with no proration (see handleScheduledSwitch) — they stay on their
    // current plan until then. pending_plan carries a scheduled change, if any.
    //
    // `subscriptionLive`, NOT `has_payment_method` — see the note above. A cancelled
    // customer with a card on file belongs in the "start paying" location at the bottom,
    // which is the only place that offers a way back in.
    if (subscriptionLive) {
      const pendingPlan = billing?.pending_plan;               // 'monthly' | 'annual' | null
      const pendingEff = billing?.pending_plan_effective;
      const pendingWord = pendingPlan === 'annual' ? 'Annual' : 'Monthly';

      // This card is the plan they're currently on.
      if (billing?.plan_type === plan) {
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-primary"><Check className="w-4 h-4" /> Current Plan</div>
            {pendingPlan && pendingPlan !== plan && (
              <p className="text-xs text-muted-foreground">
                Switching to {pendingWord} on {formatDateLongUK(pendingEff)}.
              </p>
            )}
          </div>
        );
      }
      // This card is the OTHER plan, and a switch to it is already scheduled.
      if (pendingPlan === plan) {
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-primary text-sm">
              <Clock className="w-4 h-4" /> Switching here on {formatDateLongUK(pendingEff)}
            </div>
            <Button variant="ghost" size="sm" className="w-full" onClick={handleCancelScheduledSwitch}>
              Cancel this change
            </Button>
          </div>
        );
      }
      // Offer a scheduled switch to this plan (takes effect at period end).
      return (
        <Button variant="outline" className="w-full" onClick={() => handleScheduledSwitch(plan)}>
          Switch to {planWord}{saveTag}
        </Button>
      );
    }
    return (
      <div className="space-y-2">
        {billing?.card_available && (
          <Button className="w-full btn-primary" onClick={() => handleStripeCheckout(plan)}>
            <CardIcon className="w-4 h-4 mr-2" /> Pay with Card
          </Button>
        )}
        {billing?.dd_available && (
          <Button variant="outline" className="w-full" onClick={() => handleDirectDebit(plan)}>
            <Banknote className="w-4 h-4 mr-2" /> Pay by Direct Debit
          </Button>
        )}
        {!billing?.card_available && !billing?.dd_available && (
          <p className="text-sm text-muted-foreground text-center py-2">Online payment is temporarily unavailable — please contact support.</p>
        )}
      </div>
    );
  };

  const renderBillingTab = () => (
    <div className="space-y-6">
      {/* NOT ACTIVE — the banner that was missing entirely.
          Without this, a cancelled or lapsed customer saw a green tick and no route
          back. The "Your Plan" cards below now offer Pay with Card / Direct Debit
          again, so this points at them rather than repeating the buttons. */}
      {billing?.has_payment_method && !billing?.is_trial && subscriptionDead && (
        <div className="border rounded-lg p-4 flex items-center gap-4 bg-rose-500/10 border-rose-500/30">
          <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-rose-600">Your subscription isn’t active</p>
            <p className="text-sm text-muted-foreground">
              Nothing has been deleted — your people, leave and history are exactly as you
              left them. Choose a plan below to start again and restore access.
            </p>
          </div>
        </div>
      )}

      {/* PAST DUE — the subscription is alive but the last payment didn’t go through.
          Stripe is retrying; if it never succeeds the subscription is cancelled and
          access stops. Previously this showed a green “active” tick. */}
      {billing?.has_payment_method && !billing?.is_trial && subscriptionPastDue && (
        <div className="border rounded-lg p-4 flex items-center justify-between gap-4 bg-amber-500/10 border-amber-500/30">
          <div className="flex items-center gap-4">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-600">Your last payment didn’t go through</p>
              <p className="text-sm text-muted-foreground">
                We’ll try again, but access will stop if it keeps failing. Updating your
                card is usually all it takes.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="flex-shrink-0" onClick={handleManageBilling}>
            Update card
          </Button>
        </div>
      )}

      {/* Active Subscription Banner */}
      {subscriptionLive && !billing?.is_trial && !subscriptionPastDue && (
        <div className={`border rounded-lg p-4 flex items-center gap-4 ${billing?.cancel_at_period_end ? 'bg-amber-500/10 border-amber-500/20' : 'bg-green-500/10 border-green-500/20'}`}>
          {billing?.cancel_at_period_end
            ? <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            : <Check className="w-5 h-5 text-green-500 flex-shrink-0" />}
          <div>
            {billing?.cancel_at_period_end ? (
              <>
                {/* CANCELLING DURING A TRIAL IS A DIFFERENT MESSAGE.
                    "Your subscription is set to cancel · Annual plan · Access ends 15
                    August" reads like a paid year being cut short — Rob tested exactly
                    this on 31/07/2026 and came away thinking he'd paid £10 and lost it.
                    He hadn't: Stripe was still in the trial and the first charge would
                    have been on the date shown. Saying "you won't be charged" removes
                    the have-I-just-lost-money moment at the worst possible time. */}
                {subStatus === 'trialing' ? (
                  <>
                    <p className="font-medium text-amber-500">Your trial won’t continue into a subscription</p>
                    <p className="text-sm text-muted-foreground">
                      Free trial ends {formatDateLongUK(billing?.next_billing_date)} — you
                      won’t be charged. You can resume any time before then via Manage billing.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-amber-500">Your subscription is set to cancel</p>
                    <p className="text-sm text-muted-foreground">
                      {billing?.plan_type === 'annual' ? 'Annual' : 'Monthly'} plan •&nbsp;
                      Access ends {formatDateLongUK(billing?.next_billing_date)}. You can resume any time before then via Manage billing.
                    </p>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="font-medium text-green-500">Your subscription is active</p>
                <p className="text-sm text-muted-foreground">
                  {billing?.plan_type === 'annual' ? 'Annual' : 'Monthly'} plan • 
                  Next billing: {formatDateLongUK(billing?.next_billing_date)}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Scheduled plan change banner. A paying customer has scheduled a plan
          switch for their period end (no proration) — show it honestly, with the
          option to cancel before it takes effect. */}
      {billing?.pending_plan && !billing?.is_trial && (
        <div className="border rounded-lg p-4 flex items-center justify-between gap-4 bg-indigo-500/10 border-indigo-500/20">
          <div className="flex items-center gap-4">
            <Clock className="w-5 h-5 text-indigo-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-indigo-500">Plan change scheduled</p>
              <p className="text-sm text-muted-foreground">
                {billing?.plan_type === 'annual' ? 'Annual' : 'Monthly'} → {billing?.pending_plan === 'annual' ? 'Annual' : 'Monthly'} •
                Takes effect {formatDateLongUK(billing?.pending_plan_effective)}. Nothing changes until then — no charge or credit.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="flex-shrink-0" onClick={handleCancelScheduledSwitch}>
            Cancel this change
          </Button>
        </div>
      )}

      {/* Trial Banner */}
      {billing?.is_trial && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-4">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0" />
          <div>
            <p className="font-medium text-primary">
              You have {getDaysRemaining(billing.trial_ends_at)} days left in your free trial
            </p>
            <p className="text-sm text-muted-foreground">Subscribe to continue using HolidayHive</p>
          </div>
        </div>
      )}

      {/* Plans */}
      <div className="card-hive p-6">
        <h2 className="font-heading text-xl font-bold mb-4">Your Plan</h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* Monthly */}
          <div className={`border rounded-lg p-4 ${billing?.plan_type === 'monthly' ? 'border-primary bg-primary/5' : 'border-border'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="bg-primary/20 text-primary text-xs font-bold px-2 py-1 rounded">MONTHLY</span>
              <span className="font-bold text-lg">{formatCurrency(MONTHLY_PER_USER)}/user</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Pay monthly, cancel anytime</p>
            <div className="text-sm mb-4">
              <span className="font-bold">{formatCurrency(billing?.monthly_cost || 0)}/month</span>
              <span className="text-muted-foreground"> ({billing?.user_count} users)</span>
            </div>
            {renderPlanCta('monthly')}
          </div>

          {/* Annual */}
          <div className={`border rounded-lg p-4 ${billing?.plan_type === 'annual' ? 'border-primary bg-primary/5' : 'border-border'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-2">
                <span className="bg-green-500/20 text-green-500 text-xs font-bold px-2 py-1 rounded">ANNUAL</span>
                <span className="bg-amber-500/20 text-amber-500 text-xs font-medium px-2 py-1 rounded">{ANNUAL_SAVING}</span>
              </div>
              <span className="font-bold text-lg">{formatCurrency(ANNUAL_PER_USER)}/user</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Pay for the year up front and save {ANNUAL_DISCOUNT_PERCENT}%</p>
            <div className="text-sm mb-4">
              <span className="font-bold">{formatCurrency(billing?.annual_cost || 0)}/year</span>
              <span className="text-muted-foreground"> ({billing?.user_count} users)</span>
              <div className="text-green-500 text-xs">Save {formatCurrency(billing?.annual_savings || 0)}</div>
            </div>
            {renderPlanCta('annual')}
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="card-hive p-6">
        <h2 className="font-heading text-xl font-bold mb-4">Payment Method</h2>
        {billing?.has_payment_method ? (
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              {(billing.payment_method === 'gocardless' || billing.payment_channel === 'bacs') ? <Banknote className="w-5 h-5" /> : <CardIcon className="w-5 h-5" />}
              <div>
                <p className="font-medium">{(billing.payment_method === 'gocardless' || billing.payment_channel === 'bacs') ? 'Direct Debit' : 'Card Payment'}</p>
                {/* A cancelled subscription has no next billing date. Printing the old
                    one read as "you're paying until 2027" to someone who was locked
                    out — the card is kept on file, the subscription isn't. */}
                <p className="text-sm text-muted-foreground">
                  {subscriptionDead
                    ? 'Saved for next time — no payments are being taken'
                    : billing?.cancel_at_period_end && subStatus === 'trialing'
                      // Same reasoning as the banner above: during a trial there is no
                      // payment to lose, and saying so is the whole point.
                      ? `Nothing will be charged — trial ends ${formatDateLongUK(billing.next_billing_date)}`
                      : `${billing?.cancel_at_period_end ? 'Access ends' : 'Next billing'}: ${formatDateLongUK(billing.next_billing_date)}`}
                </p>
              </div>
            </div>
            {billing.payment_method === 'stripe' ? (
              <Button variant="outline" size="sm" onClick={handleManageBilling}>Manage Billing and Invoice Details</Button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleUpdateBankDetails}>Update bank details</Button>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <CardIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No payment method configured</p>
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="card-hive p-6">
        <h2 className="font-heading text-xl font-bold mb-4">Invoices</h2>
        
        
        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Invoices &amp; receipts</p>
              <p className="text-sm text-muted-foreground">View and download every invoice in the secure billing portal.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleManageBilling}>View invoices</Button>
        </div>
      </div>

      {/* -------------------------------------------------------------------
          SELF-SERVICE OFF-BOARDING (17 Jul). If already closing, this flips
          from a "Close account" door to a countdown + "Keep our account".
          ------------------------------------------------------------------- */}
      {closeStatus?.closing ? (
        <div className={`card-hive p-6 ${closeStatus.phase === 'pending' ? 'border-amber-500/30' : 'border-red-500/30'}`}>
          <h2 className={`font-heading text-xl font-bold mb-1 ${closeStatus.phase === 'pending' ? 'text-amber-500' : 'text-red-500'}`}>
            Your account is scheduled to close
          </h2>
          {/* Two phases (22/09/2026, Terms §8): 'pending' = cancelled but paid up,
              nothing locked until close_on; 'grace' = closed, 30 days to the purge. */}
          {closeStatus.phase === 'pending' ? (
            <p className="text-sm text-muted-foreground mb-4">
              Your account stays open until <strong>{formatDateUK(closeStatus.close_on)}</strong>
              {typeof closeStatus.days_left === 'number' && <> — {closeStatus.days_left} day(s) left</>},
              the end of the period you've paid for. On that date your employees lose access, and
              everything is permanently deleted on <strong>{formatDateUK(closeStatus.purge_on)}</strong>.
              Until then nothing changes, and you can undo this any time.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mb-4">
              Everything is permanently deleted on <strong>{formatDateUK(closeStatus.purge_on)}</strong>
              {typeof closeStatus.days_left === 'number' && <> — {closeStatus.days_left} day(s) left</>}.
              Nothing has been deleted yet. Bring the account back any time before then.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <Button className="btn-primary" onClick={handleReinstate} disabled={reinstating}>
              {reinstating ? 'Working…' : 'Keep our account'}
            </Button>
            <Button variant="outline" onClick={downloadOwnData}>
              <Download className="w-4 h-4 mr-2" /> Download all our data
            </Button>
          </div>
        </div>
      ) : (
        <div className="card-hive p-6 border-red-500/20">
          <h2 className="font-heading text-lg font-bold mb-1">Close account</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Leaving HolidayHive? You can download all your data and close your
            company account here. Your account stays open to the end of the period
            you've paid for, then there's a 30-day grace period — nothing is
            deleted straight away, and you can undo it.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={downloadOwnData}>
              <Download className="w-4 h-4 mr-2" /> Download all our data
            </Button>
            <Button variant="outline" className="text-red-500 border-red-500/40 hover:bg-red-500/10"
                    onClick={() => setShowCloseDialog(true)} data-testid="open-close-account">
              Close account…
            </Button>
          </div>
        </div>
      )}

      <CloseAccountDialog
        open={showCloseDialog}
        onOpenChange={setShowCloseDialog}
        onClosed={() => billingApi.accountStatus().then((r) => setCloseStatus(r.data)).catch(() => {})}
      />
    </div>
  );

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl font-bold">Settings</h1>
                <p className="text-sm text-muted-foreground">Manage your company settings</p>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="px-6 flex gap-1 border-b border-border">
            {settingsTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        <div className={`p-6 ${activeTab === 'workinghours' ? 'max-w-6xl' : activeTab === 'managers' ? 'max-w-6xl' : 'max-w-4xl'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !isAdmin ? (
            <div className="card-hive p-8 text-center">
              <Settings className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-heading font-bold mb-2">Admin Access Required</h2>
              <p className="text-muted-foreground">Only administrators can modify company settings.</p>
            </div>
          ) : (
            <>
              {activeTab === 'general' && renderGeneralTab()}
              {activeTab === 'leave' && renderLeaveTab()}
              {activeTab === 'health' && <PerfectionBox />}
              {activeTab === 'approvals' && renderApprovalsTab()}
              {activeTab === 'leavetypes' && renderLeaveTypesTab()}
              {activeTab === 'blackouts' && renderBlackoutsTab()}
              {activeTab === 'workinghours' && renderWorkingHoursTab()}
              {activeTab === 'managers' && renderManagerPermissionsTab()}
              {activeTab === 'security' && <SecurityTab />}
              {activeTab === 'limits' && renderLeaveLimitsTab()}
              {activeTab === 'notifications' && renderNotificationsTab()}
              {activeTab === 'birthdays' && renderBirthdaysTab()}
              {activeTab === 'integrations' && <IntegrationsTab />}
              {activeTab === 'billing' && renderBillingTab()}
              {activeTab === 'import' && <TimetasticImport />}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
